/**
 * ZapPlayer — modular video player for the Zap media-server.
 *
 * Single source of truth: editor (skip-region marker-strip, region modes,
 * A/B-loop, frame-step, zoom) + transport (HLS attach, quality switch,
 * audio normalisation, resume position, stats, PiP) + persistence.
 *
 * Consumed by:
 *   - Zap-SPA Files-View modal (instantiated in _openPlayer())
 *   - standalone embed pages (auto-mounted via embed-mount.js IIFE)
 *
 * Both consumers pass the same options shape; differences are limited to
 * the `features.*` toggles (e.g. Filmdir disables Cast).
 */
import {
  loadEditorSettings,
  readRegionCache,
  writeRegionCache,
  getPlayerSetting,
  setPlayerSetting,
} from './core/persistence.js';
import { resolveRawPath, srvCreate, srvList } from './core/regions.js';
import { enterRegion, exitRegion, handleActiveRegion } from './core/state-machine.js';
import { createFader } from './core/fade.js';
import { autoPanDuringPlayback } from './core/timeline.js';
import { attachKeyboard } from './core/keyboard.js';

import { injectCss } from './ui/css.js';
import { createStrip, renderStripContent, attachStripHandlers } from './ui/strip.js';
import { createBanner, renderBanner } from './ui/banner.js';
import { createPopover, openRegionPopover, attachPopoverOutsideClick } from './ui/popover.js';
import { attachFullscreenInterceptor } from './ui/fullscreen.js';
import { readResumePos, attachResumeWriter, createResumeBanner } from './ui/resume.js';
import { createModalShell } from './ui/modal.js';
import { closeDirectStream, openDirectStream, withToken } from '../core/direct-stream.js';
import { createToolbar } from './ui/toolbar.js';
import { createControls } from './ui/controls.js';
import { createSettingsPanel } from './ui/settings.js';
import { openHelp as openHelpOverlay } from './ui/help.js';
import { createSkipUi } from './ui/skipbutton.js';

import { fetchCoverMeta } from './transport/cover.js';
import { startHlsSession, phaseLabel } from './lifecycle.js';
import { toast } from '../core/toast.js';
import { setSourceBadge } from './ui/source-badge.js';
import { attachCastButtons } from '../core/cast.js';
import { attachZapdEngine } from './zapd-engine-bridge.js';
import { SubtitleController } from './subtitles/subtitle-controller.js';
import { streamClose, streamOpen, switchTracks, tracks as zapdTracks } from './backend.js';
import { createDirectIo } from './directsource/direct-io.ts';

const DEFAULT_FEATURES = Object.freeze({
  hls: true,
  embedded: true, // auto bei Shell-Detection; false = embedded mode aus (Web-Player erzwingen)
  cast: true,
  pip: true,
  stats: true,
  resume: true,
  fade: true,
  regions: true,
  editor: true,
  fullscreen: true,
  toolbar: true,
  settings: true,
  help: true,
  skipUi: true, // Z3: "Intro überspringen"-Button auf auto-Detektionen
});

// ── Audio-track auto-pick by browser-language ────────────────────────────
// ISO-639-1 (2-letter, navigator.language) → ISO-639-2 (3-letter B + T forms,
// which is what mkv/mp4 LANGUAGE-tags carry). We accept both bibliographic
// and terminology forms because real files use both inconsistently.
const ISO639_1_TO_2 = {
  de: ['ger', 'deu'],
  en: ['eng'],
  fr: ['fre', 'fra'],
  es: ['spa'],
  it: ['ita'],
  pt: ['por'],
  nl: ['dut', 'nld'],
  ru: ['rus'],
  ja: ['jpn'],
  zh: ['chi', 'zho'],
  ko: ['kor'],
  pl: ['pol'],
  tr: ['tur'],
  ar: ['ara'],
  sv: ['swe'],
  no: ['nor', 'nob', 'nno'],
  da: ['dan'],
  fi: ['fin'],
  el: ['gre', 'ell'],
  cs: ['cze', 'ces'],
  hu: ['hun'],
  ro: ['rum', 'ron'],
  uk: ['ukr'],
  he: ['heb'],
  hi: ['hin'],
  th: ['tha'],
  vi: ['vie'],
  id: ['ind'],
  bg: ['bul'],
  ca: ['cat'],
  hr: ['hrv'],
  sr: ['srp'],
  sk: ['slo', 'slk'],
  sl: ['slv'],
  et: ['est'],
  lv: ['lav'],
  lt: ['lit'],
  is: ['ice', 'isl'],
  mt: ['mlt'],
  ga: ['gle'],
  fa: ['per', 'fas'],
  ur: ['urd'],
  bn: ['ben'],
  ta: ['tam'],
};

/**
 * Returns the user's preferred ISO-639-2 language code(s) based on
 * navigator.languages. Used für BluRay-server audio_lang field damit
 * server kann libbluray's language-info nutzen, was ffmpeg's bluray-
 * demuxer NICHT propagiert (= probe sieht "und" für alle BD-tracks).
 * Returns array of 3-char codes (= terminology + bibliographic forms),
 * leer wenn kein bekanntes mapping. First element ist top-preference.
 */
export function preferredAudioLangs639_2() {
  const langs =
    typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
      ? navigator.languages
      : [typeof navigator !== 'undefined' ? navigator.language : 'en'];
  const out = [];
  for (const tag of langs) {
    const two = String(tag || '')
      .toLowerCase()
      .slice(0, 2);
    const three = ISO639_1_TO_2[two];
    if (three) {
      for (const c of three) {
        if (!out.includes(c)) out.push(c);
      }
    }
  }
  return out;
}

/**
 * Pick the audio-track index that matches the user's preferred language.
 * `streams` is the array from probe (each has `language` ISO-639-2 string).
 * Returns -1 when no match (= caller falls back to track 0).
 *
 * Tie-breakers within the same language: prefer non-commentary tracks
 * (= those without "commentary" / "kommentar" / "audio description" in
 * title), then higher channel-count.
 */
function pickAudioTrackByLanguage(streams) {
  if (!Array.isArray(streams) || streams.length === 0) return -1;
  const langs =
    typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
      ? navigator.languages
      : [typeof navigator !== 'undefined' ? navigator.language : 'en'];
  const isCommentary = (t) => {
    const s = String(t || '').toLowerCase();
    return (
      s.includes('commentary') ||
      s.includes('kommentar') ||
      s.includes('audio description') ||
      s.includes('audiodeskription')
    );
  };
  for (const tag of langs) {
    const two = String(tag || '')
      .toLowerCase()
      .slice(0, 2);
    const targets = ISO639_1_TO_2[two];
    if (!targets) continue;
    // Find all streams whose language matches any of the target codes.
    const matches = streams
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => {
        const l = String(s.language || '').toLowerCase();
        return targets.includes(l);
      });
    if (matches.length === 0) continue;
    // Prefer non-commentary, then higher channel-count.
    matches.sort((a, b) => {
      const aCom = isCommentary(a.s.title || a.s.name);
      const bCom = isCommentary(b.s.title || b.s.name);
      if (aCom !== bCom) return aCom ? 1 : -1;
      const aCh = Number(a.s.channels || 0);
      const bCh = Number(b.s.channels || 0);
      return bCh - aCh;
    });
    return matches[0].i;
  }
  return -1;
}

export class ZapPlayer {
  /**
   * Two construction styles:
   *
   *   new ZapPlayer({ mount: 'modal', name, path, streamUrl, directUrl, ext,
   *                   getToken, onClose, features })
   *     — Player builds its own modal shell with header/toolbar/footer.
   *       The internal <video> is created by createModalShell().
   *
   *   new ZapPlayer({ mount: 'inline', videoEl, path, streamUrl, ext, ... })
   *     — Player mounts on an existing <video> element. No modal chrome.
   *       UI overlays (strip/banner/popover/stats/resume) attach to
   *       videoEl.parentElement (or opts.container).
   *
   * Legacy: new ZapPlayer(videoEl, opts) is still supported and treated as
   * mount: 'inline' (standalone embed mode).
   *
   * @param {Object|HTMLVideoElement} arg1   options object (preferred) or legacy videoEl
   * @param {Object} [arg2]                  legacy opts when arg1 is a videoEl
   */
  constructor(arg1, arg2) {
    let opts;
    if (arg1 instanceof HTMLVideoElement) {
      opts = { ...(arg2 || {}), videoEl: arg1, mount: arg2?.mount || 'inline' };
    } else {
      opts = arg1 || {};
    }

    this.opts = opts;
    this.features = { ...DEFAULT_FEATURES, ...(opts.features || {}) };
    this._destroyed = false;
    // Destroy-AbortController (Robustness): bricht in-flight Control-Plane-Calls
    // (start/state/...) beim Modal-Close/View-Leave SOFORT ab, statt sie bis
    // zum fetch-Timeout weiterlaufen zu lassen.
    this._abort = new AbortController();
    this._cleanups = [];
    this.name = opts.name || '';
    this.rawPath = opts.path || '';
    this.getToken = opts.getToken || (() => '');
    // Optional token-change subscription (auth.onTokenChange). In embedded mode
    // we forward the refreshed token to the native engine before TTL expiry.
    this.onTokenChange = typeof opts.onTokenChange === 'function' ? opts.onTokenChange : null;

    if (opts.mount === 'modal') {
      this._mountModal(opts);
    } else {
      this._mountInline(opts);
    }
    if (!this.rawPath) this.rawPath = resolveRawPath(this.video);

    if (!this.stage.style.position) this.stage.style.position = 'relative';

    // Editor settings — re-read each access so future Settings-Gear changes
    // take effect without a remount.
    this._editorSettings = loadEditorSettings();

    // Region playback state
    this.regions = this.features.regions ? readRegionCache(this.rawPath) : [];
    this.videoFps = 24;

    const video = this.video;
    this.timeline = {
      get duration() {
        return video.duration || 0;
      },
      zoomLevel: 1,
      panOffsetT: 0,
    };

    this.loop = { loopState: 'none', loopStart: 0, loopEnd: 0 };

    this.engineState = { activeRegion: null, cycleCounter: 0, preEnterRate: 1.0 };

    this.lastHoverT = null;

    // Transport / persistent player settings (shared with Filmdir)
    this.playerSettings = {
      quality: getPlayerSetting('Quality', 'auto'),
      speed: parseFloat(getPlayerSetting('Speed', '1')) || 1,
      volume: parseFloat(getPlayerSetting('Volume', '0.85')) || 0.85,
      muted: getPlayerSetting('Muted', '0') === '1',
      loop: getPlayerSetting('Loop', '0') === '1',
      audionorm: getPlayerSetting('AudioNorm', '0') === '1',
      fadeSkip: getPlayerSetting('FadeSkip', '1') !== '0',
      // Multi-track plan (Phase A, 2026-05-04). audioTrack is the audio-only
      // ordinal (ffmpeg's `0:a:N`), subtitleTrack is the absolute container
      // stream index (ffmpeg's `0:N`) or -1 for "off". Persisted globally
      // because most users want a consistent default ("always German audio").
      audioTrack: parseInt(getPlayerSetting('AudioTrack', '0'), 10) || 0,
      subtitleTrack: parseInt(getPlayerSetting('SubtitleTrack', '-1'), 10),
      audioMode: getPlayerSetting('AudioMode', 'stereo'),
      // Source mode (2026-06-18). 'direct': the native engine opens the ORIGINAL
      // container — all audio/subtitle tracks (→ live select_stream track-switch)
      // + 10-bit HDR, and the original is always seekable (no stall). Since S0-1
      // that container comes from a `stream/open` session, not from /media (the
      // route is decommissioned); the mode name stayed.
      // 'stream': server HLS (flat: video + default audio, 8-bit; adaptive bitrate /
      // weak-client compat — but seeking a COLD position stalls on the server's
      // transcode warmup, and it carries no alt-tracks/HDR). DEFAULT = direct in
      // EMBEDDED (native demuxer present; the UHD library wants full quality + the
      // warmup-seek-stall is avoided) vs stream in the browser (WASM, no demuxer).
      // opts.sourceMode / persisted SourceMode override. The embedded default
      // (direct) is applied in the play path, NOT here — getShell() is not yet
      // apiVersion-ready in the ctor (async WebChannel handshake) so it reads null.
      sourceMode: opts.sourceMode || getPlayerSetting('SourceMode', 'stream'),
      // Browser-DirectPlay (A-P1.1). When true (default), the WASM player tries
      // to demux the ORIGINAL container over /stream byte-ranges (instant seeks,
      // no server transcode) and falls back to HLS on any ineligibility. Force
      // HLS via SourceMode-style toggle OR `?direct=0`.
      directPlay: getPlayerSetting('DirectPlay', '1') !== '0',
    };
    // Browser-language auto-pick (Zappiti-style): wenn user noch NIE
    // explizit eine audio-track gewählt hat, pick die spur die zur
    // browser-sprache passt (sobald _probeAudioStreams aufgelöst ist).
    // localStorage.getItem returns null = nie gesetzt → auto-mode.
    this._audioTrackAuto = getPlayerSetting('AudioTrack', null) === null;
    if (!Number.isFinite(this.playerSettings.subtitleTrack)) this.playerSettings.subtitleTrack = -1;
    if (!['stereo', 'surround', 'passthrough'].includes(this.playerSettings.audioMode)) {
      this.playerSettings.audioMode = 'stereo';
    }
    if (!['stream', 'direct'].includes(this.playerSettings.sourceMode)) {
      this.playerSettings.sourceMode = 'stream';
    }
    // Populated by _tryDirectPlay's first /api/probe call; the Settings UI
    // reads these to render the audio/subtitle dropdowns.
    this._probeAudioStreams = [];
    this._probeSubtitleStreams = [];

    this.fadeAndSkip =
      this.features.fade && this.playerSettings.fadeSkip
        ? createFader(this.video)
        : (t) => {
            try {
              this.video.currentTime = t;
            } catch {
              /* noop */
            }
          };

    if (this.features.editor || this.features.regions) {
      injectCss();
      this._mountUI();
      this._wireEvents();
      this._initialServerSync();
    }
    this._applyTransportSettings();
    // Source-attach is async (probe call for direct-play). Defer the
    // autoplay-triggering steps until it's done, otherwise video.play()
    // runs on an empty element and Chrome silently drops it — that was
    // the "I have to click play" bug.
    this._sourceReady = Promise.resolve(this._maybeAttachSource());
    this._sourceReady.then(() => {
      // 2026-05-08: resume-banner wird NICHT mehr direkt nach
      // source-attach gemounted — sonst blockt es den warmup-overlay.
      // Stattdessen erst nach dem ersten user-play-click (= togglePlay
      // sets _userInitiatedPlay → _maybeMountResume + auto-dismiss 8s).
      this._maybeRenderMediaInfo();
    });
    this._maybeFetchCoverMeta();
    this._wireTransportPersistence();
    this._maybeMountSettings();
    this._maybeMountToolbar();
    if (this.features.resume) {
      const detach = attachResumeWriter(this.video, this.rawPath);
      this._cleanups.push(detach);
    }

    if (typeof window !== 'undefined' && window.console) {
      console.log('[ZapPlayer] mounted', {
        path: this.rawPath,
        mount: opts.mount,
        features: this.features,
      });
    }

    // Debug-Recorder optional (nur bei localStorage.zapDebug=='1') —
    // exposes window.__zapDebug ring-buffer + window.zapDumpState() für
    // browser-use-driven Diagnose. Production-Overhead: 0 (Module wird
    // nur dann geladen).
    if (typeof window !== 'undefined' && window.localStorage?.zapDebug === '1') {
      import('./debug.js')
        .then((m) => {
          try {
            m.attach(this);
          } catch (e) {
            console.warn('zapDebug attach', e);
          }
        })
        .catch((e) => {
          console.warn('zapDebug import', e);
        });
    }
  }

  // ── Mount strategies ─────────────────────────────────────────────────────
  _mountModal(opts) {
    this._modal = createModalShell({
      name: this.name,
      hlsBadge: true,
      vlcUrl: '', // filled asynchronously by _fillVlcUrl (stream session)
      onClose: () => {
        this.destroy();
      },
    });
    // Audit-Befund F6: die Stream-Session wird LAZY geoeffnet — erst wenn jemand
    // das URL-Feld anfasst. Vorher lief bei jedem Modal-Mount eine Session auf,
    // die niemand angefordert hatte und die (absichtlich) offen bleibt, damit ein
    // kopierter Link das Modal ueberlebt; der Reaper raeumte sie nach ~5 min.
    this._vlcUrlPending = false;
    const urlInput = this._modal?.urlInput;
    if (urlInput) {
      const fill = () => {
        if (this._vlcUrlPending || urlInput.value) return;
        this._vlcUrlPending = true;
        void this._fillVlcUrl(opts);
      };
      urlInput.addEventListener('focus', fill);
      urlInput.addEventListener('pointerdown', fill);
      this._cleanups.push(() => {
        urlInput.removeEventListener('focus', fill);
        urlInput.removeEventListener('pointerdown', fill);
      });
    }
    this.video = this._modal.video;
    this.stage = this._modal.stage;
    this._modalMode = true;

    // Phase banner sits between the <video> stage and the footer.
    // 2026-05-08: previously inserted before this._modal.info, which the
    // newer createModalShell no longer returns. Use footer-anchor instead.
    // Hidden by default; _setPhase() flips visibility.
    const banner = document.createElement('div');
    banner.className = 'zap-hls-phase';
    banner.hidden = true;
    const anchor = this._modal.footer || this._modal.stage;
    if (anchor && anchor.parentElement) {
      anchor.parentElement.insertBefore(banner, anchor);
    }
    this._phaseBanner = banner;

    // Custom Bottom-Controls-Bar (Plan v2 2026-05-02). Replaces the native
    // HTML5 <video controls> and the previous gear-overlay. Mounted into
    // the stage as sibling of <video> so it survives stage-fullscreen.
    this._controls = createControls(this, this._modal.controlsHost, {});
    this._cleanups.push(() => this._controls?.dispose?.());

    // Cast/AirPlay wiring (2026-05-03). The Cast button is rendered by
    // createControls() as <button data-cast-launcher hidden> between the
    // Speed dropdown and the Settings gear. attachCastButtons() looks up
    // [data-cast-launcher] within the given overlay and toggles `hidden`
    // based on CAF SDK state (so it stays invisible until a Chromecast/
    // AppleTV is actually advertised on the network). In Safari it takes
    // the AirPlay path instead — no SDK load, native AirPlay icon shows
    // up on <video>.
    if (this.features.cast !== false) {
      try {
        attachCastButtons(this._modal.controlsHost, {
          mediaUrl: this._buildCastUrl(),
          title: this.name,
          poster: null,
          getCurrentTime: () => this.video?.currentTime,
        });
      } catch (e) {
        console.debug('[ZapPlayer] cast wiring skipped:', e?.message || e);
      }
    }
  }

  _mountInline(opts) {
    if (!opts.videoEl || !(opts.videoEl instanceof HTMLVideoElement)) {
      throw new Error('[ZapPlayer] inline mount needs opts.videoEl (HTMLVideoElement)');
    }
    this.video = opts.videoEl;
    this.stage = opts.container || this.video.parentElement || document.body;
    this._modalMode = false;
  }

  // ── URL builders ─────────────────────────────────────────────────────────
  /** Footer URL for mpv/VLC: a zapd stream session for the original file with
   *  the token as query (external players cannot send headers). The former
   *  /media builders are gone with the route (S0-1). The session is deliberately
   *  NOT closed on destroy(): a copied link must outlive the modal, and zapd's
   *  ~5 min idle reaper collects it anyway. Called LAZY (first focus/pointerdown
   *  on the URL field, Audit-Befund F6), not on every modal mount. */
  async _fillVlcUrl(opts) {
    const p = opts.path || this.rawPath;
    const input = this._modal?.urlInput;
    if (!input || !p || !p.startsWith('/')) return;
    try {
      const s = await openDirectStream(p);
      if (this._destroyed) {
        void closeDirectStream(s.sessionId);
        return;
      }
      input.value = withToken(s.streamUrl);
    } catch {
      /* footer stays empty — the player itself does not depend on it */
    }
  }

  _buildCastUrl() {
    // Cast-URL = direct stream-URL (Chromecast etc.). zapd-HLS-URLs
    // hängen an einer session-id, sind nicht für externe Cast-receiver
    // geeignet. Falls hls-feature aktiv aber kein streamUrl: leer.
    return this._withTokenQuery(this.opts.streamUrl || '');
  }

  // ── Toolbar / Settings mount ─────────────────────────────────────────────
  _maybeMountToolbar() {
    if (!this.features.toolbar) return;
    if (this._modalMode) {
      // Modal-mode (2026-05-02 YouTube-style consolidation): no header toolbar.
      // The gear button rendered by createModalShell drives the unified
      // settings panel; cast/copy-url/pip/stats live there too.
      const gear = this._modal?.gearBtn;
      if (gear) {
        const onGear = () => this._toggleSettings();
        gear.addEventListener('click', onGear);
        this._cleanups.push(() => gear.removeEventListener('click', onGear));
      }
      return;
    }
    // Inline mode (Filmdir-detail, share-link, etc.) keeps the legacy
    // floating toolbar — those contexts have no native modal frame.
    const host = document.createElement('div');
    host.className = 'zap-toolbar-inline';
    this.stage.appendChild(host);
    this._toolbar = createToolbar(this, host, {
      features: this.features,
      castMediaUrl: this._buildCastUrl(),
      name: this.name,
      poster: null,
      getCurrentTime: () => this.video?.currentTime,
      onToggleSettings: () => this._toggleSettings(),
      initialLoop: this.playerSettings.loop,
    });
  }

  _maybeMountSettings() {
    if (!this.features.settings) return;
    // Audio + subtitle dropdowns now live in the bottom controls-bar
    // (createControls) — this panel only carries the secondary toggles
    // plus Copy-URL/Help.
    this._settings = createSettingsPanel(this, {
      initialValues: { ...this.playerSettings },
      features: this.features,
      castMediaUrl: this._buildCastUrl(),
    });
    this.stage.appendChild(this._settings.panel);

    // Cast button now lives in the bottom Controls-Bar (createControls);
    // attachCastButtons() is called from _mountModal directly. Inline-mode
    // (Filmdir) keeps its legacy toolbar-attached cast-button via
    // _maybeMountToolbar() → createToolbar().

    // Click outside the panel + gear-button closes it (YouTube behaviour).
    if (this._modalMode) {
      const onOutside = (e) => {
        if (!this._settings || this._settings.panel.hidden) return;
        if (this._settings.panel.contains(e.target)) return;
        if (this._modal?.gearBtn?.contains(e.target)) return;
        this._settings.hide();
        this._modal?.gearBtn?.classList.remove('is-active');
      };
      document.addEventListener('click', onOutside, true);
      this._cleanups.push(() => document.removeEventListener('click', onOutside, true));
    }
  }

  _toggleSettings() {
    if (!this._settings) return;
    const visible = this._settings.toggle();
    this._toolbar?.setSettingsState?.(visible);
    this._modal?.gearBtn?.classList.toggle('is-active', visible);
  }

  // ── Help-Overlay (?-Key & toolbar button) ────────────────────────────────
  openHelp() {
    if (!this.features.help) return false;
    return openHelpOverlay();
  }

  // ── UI mount (editor) ─────────────────────────────────────────────────
  _mountUI() {
    this.banner = createBanner();
    this.stage.appendChild(this.banner);

    this.strip = createStrip();
    // Plan v2 (2026-05-02): editor-strip starts hidden. User toggles with
    // E (see core/keyboard.js). Avoids the "double slider" visual when the
    // native scrubber and our editor strip stack on top of each other.
    this.strip.hidden = true;
    this._editorMode = false;
    this.stage.appendChild(this.strip);

    this.popover = createPopover();
    this.stage.appendChild(this.popover);

    if (this.features.fullscreen) {
      const detach = attachFullscreenInterceptor(this.video, this.stage, () => this.renderStrip());
      this._cleanups.push(detach);
    }

    const detachOutside = attachPopoverOutsideClick(this.popover);
    this._cleanups.push(detachOutside);

    if (this.features.skipUi) {
      this._skipUi = createSkipUi({
        stage: this.stage,
        video: this.video,
        // Lambda statt Referenz: setFadeSkip() ersetzt this.fadeAndSkip
        // komplett — eine gecapturte Referenz saehe den Settings-Toggle
        // nie (Review-Befund; die Region-Engine liest pro Tick frisch).
        fadeAndSkip: (t) => this.fadeAndSkip(t),
      });
      this._cleanups.push(() => {
        this._skipUi?.destroy();
        this._skipUi = null;
      });
    }
  }

  _maybeMountResume() {
    if (!this.features.resume) return;
    const savedPos = readResumePos(this.rawPath);
    if (!(savedPos > 30)) {
      // Autoplay disabled testweise (2026-05-02): user will click the
      // native play-button manually so the audio is unlocked under the
      // browser's user-gesture rule. Earlier autoplay made Chrome/Safari
      // mute the stream silently for some sources (e.g. 187).
      // this.video.play().catch(() => { /* user gesture missing */ });
      return;
    }
    this._pendingResumePos = savedPos;
    const banner = createResumeBanner(
      savedPos,
      () => this._applyResume(true),
      () => this._applyResume(false),
    );
    if (banner) this.stage.appendChild(banner);
  }

  _applyResume(doResume) {
    const t = this._pendingResumePos || 0;
    this._pendingResumePos = null;
    if (doResume) {
      // The "Resume"-banner button click IS a user gesture, so play() here
      // is allowed to start with sound. This is the *only* place we keep
      // an automatic play(); cold-opens (above) require an explicit click.
      const restore = () => {
        try {
          this.video.currentTime = t;
        } catch {
          /* noop */
        }
        this.video.removeEventListener('loadedmetadata', restore);
        this.video.play().catch(() => {
          /* noop */
        });
      };
      if (this.video.readyState >= 1) restore();
      else this.video.addEventListener('loadedmetadata', restore);
    } else {
      // Banner-decline: same autoplay-disable rationale as cold-open.
      // this.video.play().catch(() => { /* noop */ });
    }
  }

  /**
   * Fetches /api/probe and stashes the result on the player so two consumers
   * can read it:
   *   - `_refreshLiveInfo()` → controls-bar quality-button tooltip
   *   - zapd-bridge.setMediaInfo() → debug-overlay "Source" section
   *
   * No DOM rendering anymore — the under-player info-row was retired in
   * favour of the consolidated debug-overlay (D-key toggle).
   */
  _maybeRenderMediaInfo() {
    if (!this.rawPath) return;
    // 2026-05-08: route ALL probing through zapd. Backend's PHP-ffprobe
    // can't read libdvdread/libbluray containers — ISOs returned null
    // → "Quelle: —". zapd's MediaSession::probe() has the full lib-stack
    // (libavformat + libbluray + libdvdread) and exposes a richer schema
    // (audio_streams[], subtitle_streams[], duration_us, profile, fps,
    // pix_fmt). We map that shape into what the controls-bar expects.
    fetch('/api-zapd/probe?path=' + encodeURIComponent(this.rawPath), {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || d.error) return;
        const v = d.video || {};
        // First non-commentary audio = pick the default-flag if any,
        // else the first stream. Channel-count breakdown helps distinguish
        // 2.0 stereo from 5.1/7.1 surround.
        const audioStreams = Array.isArray(d.audio_streams) ? d.audio_streams : [];
        const a = audioStreams.find((s) => s.is_default) || audioStreams[0] || {};

        this._mediaInfo = {
          canDirectPlay: false, // zapd only reaches us when transcoding
          videoCodec: v.codec ? String(v.codec).toUpperCase() : null,
          srcWidth: v.width || null,
          srcHeight: v.height || null,
          audioCodec: a.codec ? String(a.codec).toUpperCase() : null,
          audioChannels: a.channels || null,
          container: d.container
            ? String(d.container).toUpperCase()
            : (this.opts.ext || '').replace(/^\./, '').toUpperCase() || null,
          sizeBytes: typeof d.size_bytes === 'number' && d.size_bytes >= 0 ? d.size_bytes : null,
          durationSec:
            typeof d.duration_us === 'number' && d.duration_us > 0
              ? d.duration_us / 1_000_000
              : null,
        };
        // Slim alias kept for `_refreshLiveInfo()` — controls-tooltip schema.
        this._sourceInfo = {
          codec: this._mediaInfo.videoCodec,
          width: this._mediaInfo.srcWidth,
          height: this._mediaInfo.srcHeight,
          audioCodec: this._mediaInfo.audioCodec,
          audioChannels: this._mediaInfo.audioChannels,
        };
        // Plumb to debug-overlay if the zapd-bridge is already attached.
        // Otherwise `_attachHlsSource` will re-push when the bridge is built.
        try {
          this._zapdBridge?.setMediaInfo?.(this._mediaInfo);
        } catch {
          /* noop */
        }
        this._refreshLiveInfo();
      })
      .catch(() => {
        /* noop — info panel is non-critical */
      });
  }

  /**
   * Push playback + source info to the controls-bar tooltip. Playback values
   * come from `engine.getDecodePath()` + `engine.getCurrentBitrateBps()`
   * (codec/bitrate). Source values come from /api/probe (cached in
   * `this._sourceInfo` after the initial fetch).
   *
   * Called on `loadedmetadata` and after each quality-switch reattach.
   */
  _refreshLiveInfo() {
    if (!this._controls?.updateLive) return;
    // Live size: prefer the engine's coded dims (always correct), fall
    // back to the <video>-element values (only meaningful on the legacy
    // native-MSE path which is gone since hls.js retirement).
    const sz = this._zapdBridge ? null : null; // bridge has no public size getter; fall through
    const liveSize =
      typeof window?.__zapEngine?.getVideoSize === 'function'
        ? window.__zapEngine.getVideoSize()
        : { width: null, height: null };
    const path =
      typeof window?.__zapEngine?.getDecodePath === 'function'
        ? window.__zapEngine.getDecodePath()
        : null;
    const bitrate =
      typeof window?.__zapEngine?.getCurrentBitrateBps === 'function'
        ? window.__zapEngine.getCurrentBitrateBps()
        : 0;
    const live = {
      width: liveSize.width || this.video?.videoWidth || null,
      height: liveSize.height || this.video?.videoHeight || null,
      codec: path?.videoCodec ? String(path.videoCodec).toUpperCase() : null,
      audioCodec: path?.audioCodec ? String(path.audioCodec).toUpperCase() : null,
      bitrate: bitrate || null,
    };
    const src = this._sourceInfo || {};
    this._controls.updateLive({
      ...live,
      srcCodec: src.codec,
      srcWidth: src.width,
      srcHeight: src.height,
      srcAudio: src.audioCodec
        ? `${src.audioCodec}${src.audioChannels ? ' ' + src.audioChannels + 'ch' : ''}`
        : null,
    });
  }

  _maybeFetchCoverMeta() {
    if (!this.rawPath) return;
    fetchCoverMeta(this.rawPath).then((cover) => {
      if (!cover) return;
      if (cover.poster_url) this.video.poster = cover.poster_url;
      this.cover = cover;
      this.opts.onCoverMeta?.(cover);
    });
  }

  // ── Transport ─────────────────────────────────────────────────────────
  _applyTransportSettings() {
    this.video.volume = Math.max(0, Math.min(1, this.playerSettings.volume));
    this.video.muted = this.playerSettings.muted;
    this.video.playbackRate = this.playerSettings.speed;
    this.video.loop = this.playerSettings.loop;
  }

  _wireTransportPersistence() {
    const onVol = () => {
      setPlayerSetting('Volume', String(this.video.volume));
      setPlayerSetting('Muted', this.video.muted ? '1' : '0');
    };
    const onRate = () => setPlayerSetting('Speed', String(this.video.playbackRate));
    this.video.addEventListener('volumechange', onVol);
    this.video.addEventListener('ratechange', onRate);
    this._cleanups.push(() => {
      this.video.removeEventListener('volumechange', onVol);
      this.video.removeEventListener('ratechange', onRate);
    });

    // Stage state for the loading-spinner CSS (.player-overlay__stage::after).
    // Without this the spinner sits on top of the playing video forever.
    // States: loading (default in CSS) / ready (metadata loaded) / playing.
    if (this.stage?.dataset) {
      this.stage.dataset.state = 'loading';
      const setState = (s) => {
        if (this.stage?.dataset) this.stage.dataset.state = s;
      };
      // Plan v2: also push live-format-info to the controls-bar tooltip on
      // every metadata-tick. videoWidth/videoHeight is finalised here.
      const onMeta = () => {
        setState('ready');
        try {
          this._refreshLiveInfo?.();
        } catch {}
      };
      const onPlaying = () => {
        setState('playing');
        try {
          this._refreshLiveInfo?.();
        } catch {}
      };
      // In embedded playback the native engine renders below the WebView and
      // owns buffering — a 'waiting' here would light the stage ::after spinner
      // ON TOP of the native video (flicker + black). Ignore once started.
      const onWaiting = () => {
        if (this._embedded && this._embeddedStarted) return;
        setState('loading');
      };
      this.video.addEventListener('loadedmetadata', onMeta);
      this.video.addEventListener('playing', onPlaying);
      this.video.addEventListener('waiting', onWaiting);
      this._cleanups.push(() => {
        this.video.removeEventListener('loadedmetadata', onMeta);
        this.video.removeEventListener('playing', onPlaying);
        this.video.removeEventListener('waiting', onWaiting);
      });
    }

    // Restore the saved muted/volume on first playing-event. Chrome's
    // autoplay-policy will auto-mute autoplay-with-sound on cold visits
    // (low Media Engagement Index) — that's what produced the "187 hat
    // kein Audio" report: pipeline was fine, the element just got
    // started in muted state and our _applyTransportSettings (which ran
    // BEFORE the source attached) couldn't override it. Re-applying on
    // the first 'playing' event takes ownership back from the browser.
    let firstPlay = true;
    const onFirstPlaying = () => {
      if (!firstPlay) return;
      firstPlay = false;
      // Only undo browser auto-mute; don't disturb explicit user mute
      // saved from a previous session (Muted=1 in localStorage).
      if (this.video.muted && !this.playerSettings.muted) {
        this.video.muted = false;
      }
      if (this.video.volume === 0 && this.playerSettings.volume > 0) {
        this.video.volume = this.playerSettings.volume;
      }
    };
    this.video.addEventListener('playing', onFirstPlaying);
    this._cleanups.push(() => this.video.removeEventListener('playing', onFirstPlaying));
  }

  async _maybeAttachSource() {
    if (!this.opts.streamUrl && !this.rawPath) return;
    // ZAPD-ONLY (2026-05-06): direct-play-fallback ist KOMPLETT entfernt.
    // Auch h264+AAC-files gehen durch zapd's HLS-pipeline weil:
    //   1. Manifest-from-keyframes liefert exakte EXTINFs für instant-seek
    //   2. seek-debounce + indexing-overlay funktioniert nur über ZapPlayer
    //   3. Direct-mkv-stream im Browser-native-Player umgeht alle unsere
    //      seek/buffer/UI-features (war der Bug aus dem Bericht
    //      "spielt 2-3s und Schluss" — direct-MKV mit DTS-audio failed
    //      stumm im Chrome-native-Player).
    // Ein kanonischer Dateipfad startet immer eine neue zapd-Session. Eine
    // vorab konstruierte PHP-era-Playlist-URL ist dafür nicht erforderlich.
    if (this.rawPath && this.features.hls) {
      // Probe trotzdem ausführen damit _probeAudioStreams +
      // _probeSubtitleStreams gefüllt sind (Settings-dropdown brauchen das).
      try {
        await this._tryDirectPlay();
      } catch {}
      this._isDirectPlay = false;
      this._attachHlsSource();
    } else {
      // Non-HLS streamUrl (z.B. share-links): native attach.
      this.video.src = this._withTokenQuery(this.opts.streamUrl);
    }
  }

  /**
   * Probe the file to see if direct-play is viable. Returns the direct URL
   * if so, otherwise null. Conservative: only attempts when the user hasn't
   * forced a quality cap or audio-normalisation (those need ffmpeg).
   */
  async _tryDirectPlay() {
    if (!this.rawPath) return null;
    // Cache probe response on the player instance so the Settings UI can
    // populate audio/subtitle dropdowns from the same data without a 2nd call.
    try {
      // 2026-05-08: route through zapd (handles ISOs/BluRay/DVD too).
      // zapd's schema is the canonical truth — no PHP fallback.
      const r = await fetch('/api-zapd/probe?path=' + encodeURIComponent(this.rawPath), {
        credentials: 'include',
      });
      if (!r.ok) return null;
      const d = await r.json();
      if (!d || d.error) return null;
      this._probeAudioStreams = Array.isArray(d.audio_streams) ? d.audio_streams : [];
      this._probeSubtitleStreams = Array.isArray(d.subtitle_streams) ? d.subtitle_streams : [];

      // Browser-language auto-pick (Phase 6f): pick the first audio-stream
      // whose language matches navigator.languages, but only if user has
      // never explicitly chosen a track. After this, _audioTrackAuto stays
      // true — when the user later picks a track manually setAudioTrack
      // calls setPlayerSetting('AudioTrack',...) which makes future loads
      // pick up that exact track instead.
      if (this._audioTrackAuto) {
        const idx = pickAudioTrackByLanguage(this._probeAudioStreams);
        if (idx >= 0) this.playerSettings.audioTrack = idx;
      }

      // Push stream lists to the controls-bar so the Audio + Subtitle
      // dropdowns can populate themselves (and reveal the buttons if
      // there are multi-track choices to make). The probe may resolve
      // after createControls(), so this is the wiring that lights them up.
      try {
        this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
          audioTrack: this.playerSettings?.audioTrack ?? 0,
          subtitleTrack: this.playerSettings?.subtitleTrack ?? -1,
        });
      } catch {}

      // Direct-play eligibility — be conservative. Backend already disqualifies
      // multi-audio + extractable-sub files (handleProbe sets canDirectPlay
      // false), so trust its decision plus the user's transport prefs.
      const q = this.playerSettings?.quality;
      if (q && q !== 'auto' && q !== 'max') return null;
      if (this.playerSettings?.audionorm) return null;
      if (this.playerSettings?.subtitleTrack >= 0) return null;
      if ((this.playerSettings?.audioTrack ?? 0) > 0) return null;
      if (d?.canDirectPlay && d?.directUrl) return d.directUrl;
    } catch {
      /* noop — fall back to HLS */
    }
    return null;
  }

  _looksLikeHls(url, ext) {
    if (!url) return false;
    if (url.includes('/hls/')) return true;
    if (url.includes('.m3u8')) return true;
    if (ext === 'm3u8') return true;
    return false;
  }

  _withTokenQuery(url) {
    const tok = this.getToken();
    if (!tok || !url) return url;
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'token=' + encodeURIComponent(tok);
  }

  /**
   * A-P1.1 Browser-DirectPlay eligibility. Conservative v1: any server-side
   * processing need (quality cap, audio-norm, burned/extracted subs, ABR
   * ladder) forces HLS, and the SAB plumbing needs crossOriginIsolated.
   */
  _directEligible() {
    // The reason is logged: several gates read GLOBALLY persisted settings (a
    // subtitle or quality pick from weeks ago silently forces HLS on every
    // film) — without the log that reads as "direct is broken".
    const no = (reason) => {
      console.info(`[zap-player] direct ineligible: ${reason} → HLS`);
      return false;
    };
    if (!this.playerSettings?.directPlay) return no('DirectPlay-Setting aus');
    try {
      if (new URLSearchParams(window.location.search).get('direct') === '0') return no('?direct=0');
    } catch {
      /* noop */
    }
    if (this._embedded) return no('embedded shell');
    const q = this.playerSettings?.quality;
    if (q && q !== 'auto') return no(`Aufloesung "${q}" statt Auto (persistiert)`);
    if (this.playerSettings?.audionorm) return no('Audio-Normalisierung aktiv (persistiert)');
    if ((this.playerSettings?.subtitleTrack ?? -1) >= 0)
      return no('Untertitel aktiv (persistiert) — Subs brauchen v1 den HLS-Pfad');
    if (typeof this.opts?.abr === 'number') return no('ABR-Level erzwungen');
    if (typeof this.opts?.transcode === 'string') return no('Transcode-Option gesetzt');
    if (typeof SharedArrayBuffer === 'undefined' || !window.crossOriginIsolated)
      return no('kein crossOriginIsolated/SAB');
    return true;
  }

  /**
   * A-P1.1: open a raw /stream session + SAB-AVIO plumbing and attach the
   * engine in direct mode. Returns true when playback came up; false means
   * "fall back to HLS" (session closed, io disposed — no user-visible error,
   * that is the DESIGNED degradation for unsupported codecs/HDR and for the
   * deployed wasm that lacks zapd_open_stream).
   */
  async _attachDirectSource() {
    // One-shot phase breadcrumbs (main-thread side). Pairs with the worker's
    // [zapd-direct] logs so a stalled attach shows exactly which side/phase
    // never advanced. One line per phase — never in a loop.
    const dt0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const dlog = (phase) =>
      console.info(
        `[zap-player] direct +${Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - dt0)}ms ${phase}`,
      );
    let open;
    try {
      open = await streamOpen({ path: this.rawPath, signal: this._abort?.signal });
    } catch (e) {
      console.info('[zap-player] direct: stream/open failed → HLS', e?.message || e);
      return false;
    }
    dlog(`stream/open OK (fileSize=${open.fileSize})`);
    // Old zapd without file_size in the open-response → not direct-capable
    // (deploy-order-independent: frontend may ship before the daemon).
    if (!(open.fileSize > 0)) {
      streamClose(open.sessionId);
      return false;
    }
    const mkIo = (o) =>
      createDirectIo({
        url: new URL(o.streamUrl, window.location.href).toString(),
        fileSize: o.fileSize,
        token: (() => {
          try {
            return this.getToken() || null;
          } catch {
            return null;
          }
        })(),
      });
    let io;
    try {
      io = mkIo(open);
    } catch (e) {
      console.info('[zap-player] direct: io setup failed → HLS', e?.message || e);
      streamClose(open.sessionId);
      return false;
    }

    // Initial audio: user's ordinal → GLOBAL source stream index (probe's
    // `index` IS the demuxer's StreamInfo.index — same container).
    const ord = this.playerSettings?.audioTrack ?? 0;
    const track = this._probeAudioStreams?.[ord];
    const audioStreamIndex = track && Number.isFinite(track.index) ? track.index : undefined;

    dlog('io ready → attachZapdEngine(direct)');
    let bridge;
    try {
      bridge = await attachZapdEngine(this.video, this.stage, null, {
        contentId: this.rawPath,
        skipRenderer: false,
        direct: { io, fileSize: open.fileSize, audioStreamIndex },
        // A byte-range read 404ed mid-playback (idle-reaper / zapd restart):
        // mint a fresh sid + DirectIo; the bridge reattaches + resumes.
        onDirectRecover: async () => {
          try {
            const fresh = await streamOpen({ path: this.rawPath });
            if (!(fresh.fileSize > 0)) return null;
            const freshIo = mkIo(fresh);
            this._directSid = fresh.sessionId;
            return { io: freshIo, fileSize: fresh.fileSize };
          } catch (e) {
            console.error('[zap-player] direct: recovery re-open failed', e);
            return null;
          }
        },
        onError: (e) => {
          console.error('[zapd-engine]', e);
        },
        onFirstFrame: () => {
          this._isDirectPlay = false;
        },
        onAudioSwitchFailed: (reason) => this._onAudioSwitchFailed(reason),
      });
    } catch (err) {
      // attachZapdEngine already tore down its canvas + engine (io included)
      // on the direct path; dispose here too (idempotent) for early throws.
      try {
        io.dispose();
      } catch {
        /* noop */
      }
      streamClose(open.sessionId);
      const tag =
        err?.name === 'DirectUnsupportedError' ? `unsupported (${err.reason})` : 'attach failed';
      console.info(`[zap-player] direct: ${tag} → HLS`, err?.message || err);
      return false;
    }

    dlog('attach OK → Direct playback');
    this._directSid = open.sessionId;
    this._zapdBridge = bridge;
    // Direct seeks are demuxer-local — always ready, no keyframe-index warmup.
    this._setSeekReady(true, 0);
    this._hideWarmupOverlay();
    // The header badge is rendered as "HLS" at modal creation (mode unknown
    // then) — retitle it; a later HLS respawn resets it in _hlsDetach.
    const badge = this._modal?.header?.querySelector('.player-overlay__hls-badge');
    if (badge) badge.textContent = 'Direct';
    if (this._mediaInfo) {
      try {
        bridge.setMediaInfo(this._mediaInfo);
      } catch {
        /* noop */
      }
    }

    const onPlay = () => bridge.play();
    const onPause = () => bridge.pause();
    this.video.addEventListener('play', onPlay);
    this.video.addEventListener('pause', onPause);
    this._hlsDetach = () => {
      try {
        this.video.removeEventListener('play', onPlay);
      } catch {
        /* noop */
      }
      try {
        this.video.removeEventListener('pause', onPause);
      } catch {
        /* noop */
      }
      try {
        bridge.detach();
      } catch {
        /* noop */
      }
      // Best-effort server-side release; the 5-min idle-reaper covers a
      // dropped DELETE (tab close etc.).
      try {
        streamClose(this._directSid);
      } catch {
        /* noop */
      }
      this._directSid = null;
      if (badge) badge.textContent = 'HLS';
    };
    return true;
  }

  async _attachHlsSource() {
    if (this._hlsDetach) {
      try {
        this._hlsDetach();
      } catch {
        /* noop */
      }
      this._hlsDetach = null;
    }

    // Embedded mode (native shell) no longer runs here: openPlayer() routes a
    // present window.zapShell to NativeShellSession (the native QML player owns
    // playback — no SPA web-player). ZapPlayer is the browser / WASM path only.

    // zapd-WASM-engine ist der einzige Player-Pfad seit hls.js-Retirement
    // 2026-05-08. Errors bubbeln hoch zum Player-error-banner — kein
    // Fallback (CLAUDE.md no-workarounds).
    if (!this.rawPath || !this.features.hls) {
      throw new Error('attachZapdSource: no rawPath or hls disabled');
    }
    // Phase E: overlay SOFORT zeigen, bevor session-start + erste poll
    // antwort. Sonst sieht user „nichts passiert über sekunden" während
    // session creation + initial probe (= 1-3 s). Server publisht später
    // konkretere phasen die das label überschreiben.
    this._showWarmupOverlay('Verbindung herstellen…', 0, 0);
    // A-P1.1 Browser-DirectPlay: try demuxing the ORIGINAL container over
    // /stream byte-ranges (instant demuxer-local seeks, zero server transcode)
    // BEFORE spawning an HLS session. Every ineligibility/failure falls through
    // to the HLS path below exactly as before.
    if (this._directEligible()) {
      const ok = await this._attachDirectSource();
      if (ok) return;
    }
    if (!this._hlsSession) {
      await this._startSession();
    }
    const url = this._hlsSession.playlistUrl;

    const bridge = await attachZapdEngine(this.video, this.stage, url, {
      contentId: this.rawPath,
      skipRenderer: false, // user has real GPU
      onError: (e) => {
        // Transient retryable errors (5xx during encoder-warmup). Show
        // first 3 + every 10th to track retry-rate without flooding.
        if (e && e.transient) {
          const msg = String(e.message || e);
          if (msg !== this._lastTransientErr) {
            this._lastTransientErr = msg;
            this._transientErrCount = 1;
            console.warn('[zapd-engine]', msg);
          } else {
            this._transientErrCount = (this._transientErrCount || 0) + 1;
            if (this._transientErrCount <= 3 || this._transientErrCount % 10 === 0) {
              console.warn(`[zapd-engine] retry#${this._transientErrCount}`, msg);
            }
          }
        } else {
          this._lastTransientErr = null;
          this._transientErrCount = 0;
          console.error('[zapd-engine]', e);
        }
      },
      onFirstFrame: () => {
        this._isDirectPlay = false;
      },
      // Server-session-loss recovery (zapd OOM-restart, session-reaper).
      // We dispose the dead session-handle, request a fresh one via
      // /play/start, return the new playlist URL — engine swaps
      // playlists in place + seeks to current playback time.
      refreshSession: async () => {
        try {
          this._dropSession();
          await this._startSession();
          // Recovery swaps the engine playlist in place with a NEW sid but does
          // NOT re-run _attachHlsSource, so the subtitle controller would keep
          // the dead session's master/rendition URLs (→ subs 404). Re-point it.
          try {
            this._subtitleController?.setSession(this._hlsSession?.playlistUrl);
          } catch {
            /* noop */
          }
          return this._hlsSession?.playlistUrl ?? null;
        } catch (e) {
          console.error('[zapd-engine] refreshSession failed', e);
          return null;
        }
      },
      // Audio-track switch couldn't complete (rendition playlist failed to
      // load, or its segments wedged). The previous track keeps playing
      // where possible — revert the dropdown selection + toast so the user
      // knows it didn't take. NOT a full error (video is fine).
      onAudioSwitchFailed: (reason) => this._onAudioSwitchFailed(reason),
    });
    // The engine's detach lifecycle hooks into the same _hlsDetach slot
    // so all existing teardown-paths (track-switch, far-seek-reload,
    // session-reaper, modal-close) free the engine cleanly.
    this._hlsDetach = () => {
      try {
        bridge.detach();
      } catch {
        /* noop */
      }
    };
    // Stash bridge so setAudioTrack can call bridge.setAudioRendition()
    // for on-the-fly switching without dispose+reattach.
    this._zapdBridge = bridge;
    // Phase E1.1: pin the zapd session-id to the engine so the debug-
    // overlay can filter hls-events / system-stats per-session instead
    // of falling back to "first session in the array".
    if (this._hlsSession?.sessionHash) {
      try {
        bridge.setSessionId?.(this._hlsSession.sessionHash);
      } catch {
        /* noop */
      }
    }
    // Push probe-data immediately if it already resolved before bridge
    // attach (often the case on second-open since /api/probe is cached).
    // Otherwise `_maybeRenderMediaInfo` does the same call on resolve.
    if (this._mediaInfo) {
      try {
        bridge.setMediaInfo(this._mediaInfo);
      } catch {
        /* noop */
      }
    }
    // Drive playback once the user-gesture comes (autoplay path).
    // The native <video> is hidden but still triggers `play` event
    // when its `play()` is invoked — we forward to the engine here.
    const onPlay = () => bridge.play();
    const onPause = () => bridge.pause();
    this.video.addEventListener('play', onPlay);
    this.video.addEventListener('pause', onPause);
    // Stash so detach removes them.
    const prevDetach = this._hlsDetach;
    this._hlsDetach = () => {
      try {
        this.video.removeEventListener('play', onPlay);
      } catch {
        /* noop */
      }
      try {
        this.video.removeEventListener('pause', onPause);
      } catch {
        /* noop */
      }
      try {
        this._subtitleController?.destroy();
      } catch {
        /* noop */
      }
      this._subtitleController = null;
      prevDetach();
    };

    // F1 W4: Sidecar-WebVTT-Untertitel im Browser-Pfad. Eigenes Main-Thread-
    // Subsystem (kein Eingriff in den Worker-Decode-Hotpath); rendert ein
    // .zap-cue-overlay ueber dem Canvas. Im Shell-Pfad uebernimmt weiterhin
    // createCueOverlay via Engine-Callback (oben, attach-embedded-shell).
    this._subtitleController = new SubtitleController({
      stage: this.stage,
      video: this.video,
      getToken: () => {
        try {
          return this.getToken() || '';
        } catch {
          return '';
        }
      },
      masterUrl: url,
      initialTrack: this.playerSettings?.subtitleTrack ?? -1,
    });
  }

  // ── HLS-Session lifecycle ─────────────────────────────────────────
  // Plex/Zappiti-Pattern: server encodet nur den DEFAULT audio-track,
  // alle anderen sichtbar im UI aber on-demand-spawned via /switch.
  async _startSession() {
    // In-Flight-Guard (Robustness P1 — serialisierter Session-Owner): zwei
    // GLEICHZEITIGE _startSession (z.B. attach + recovery, oder seek-reload +
    // track-switch) wuerden je ein /play/start feuern → zwei Server-Sessions
    // (parallele Encoder + Leak, da nur eine in this._hlsSession landet). Laeuft
    // schon ein Start, geben wir denselben Promise zurueck statt einen 2. zu
    // erzeugen — die zweite dispose-before-start-Phase entfaellt damit auch.
    if (this._startInFlight) return this._startInFlight;
    this._startInFlight = this._startSessionInner();
    try {
      return await this._startInFlight;
    } finally {
      this._startInFlight = null;
    }
  }

  async _startSessionInner() {
    // Double-start guard: dispose any existing session before opening a new
    // one, so a re-entrant _startSession (re-open / recovery) never leaks the
    // previous zapd session (= the "2× same film" leak from the lifecycle
    // hand-off). dispose() is idempotent + sends the synchronous stop.
    this._dropSession();
    const heightOpt =
      this.playerSettings?.quality && this.playerSettings.quality !== 'auto'
        ? this.playerSettings.quality
        : 'auto';
    // Default-track: explicit per `audio: <stream_idx>` damit user's
    // browser-language-preference (= playerSettings.audioTrack ordinal,
    // pre-picked in zap-player ctor via `_pickPreferredAudioTrack`)
    // honored wird ab session-start. Bei missing → server fallback auf
    // find_best_stream (= main 5.1).
    const subStreams = (this._probeSubtitleStreams || [])
      .filter((s) => s.extractable !== false)
      .map((s) => s.index)
      .filter((i) => Number.isFinite(i));
    const wantAudioOrdinal = this.playerSettings?.audioTrack ?? 0;
    const wantAudioStream = this._probeAudioStreams?.[wantAudioOrdinal];
    const explicitAudioIdx =
      wantAudioStream && Number.isFinite(wantAudioStream.index) ? wantAudioStream.index : undefined;
    // 2026-05-10: audio_lang für BluRay-iso. Probe-data hat "und" für alle
    // BD-tracks weil ffmpeg's bluray-demuxer language-tags nicht propagiert.
    // Server nutzt direkt libbluray (= BLURAY_STREAM_INFO::lang) für language-
    // matching. Wir sagen ihm welche language wir wollen — first preferred
    // ISO-639-2 code aus navigator.languages.
    const preferredLangs = preferredAudioLangs639_2();
    const audioLang = preferredLangs.length > 0 ? preferredLangs[0] : undefined;
    const sess = await startHlsSession(this.video, {
      path: this.rawPath,
      startSec: 0,
      signal: this._abort?.signal, // Teardown bricht start()/state-Poll mit ab
      opts: {
        height: heightOpt,
        audionorm: !!this.playerSettings?.audionorm,
        audio_mode: this.playerSettings?.audioMode ?? 'stereo',
        audio: explicitAudioIdx,
        audio_lang: audioLang, // BluRay language preference
        subtitle_streams: subStreams,
        // Z2-ABR: Multi-Variant-Ladder anfordern, wenn der Caller abr>1 gibt
        // (Contract 02 §8). Default (undefined) → single-variant, ABR inert.
        abr: typeof this.opts?.abr === 'number' ? this.opts.abr : undefined,
        // ABR braucht den Encode-Pfad: ein browser-safe H.264-Source wird sonst
        // copy/passthrough → kein deriveAbrLadder → 1 Variante (Team-B-Befund
        // 2026-06-16). transcode:'h264' erzwingt die Rungs. Nur mitschicken,
        // wenn der Caller es setzt (abr-Pfad), sonst undefined → lifecycle 'auto'.
        transcode: typeof this.opts?.transcode === 'string' ? this.opts.transcode : undefined,
      },
      onPhase: (state, detail) => this._setPhase(state, detail),
      onSuperseded: (reason) => this._handleSuperseded(reason),
      onSeekReadyChange: (ready, etaMs) => this._setSeekReady(ready, etaMs),
      onPhaseChange: (phase, message, progressPct, etaMs) =>
        this._setLifecyclePhase(phase, message, progressPct, etaMs),
      onSourceChange: (source) => this._setSourceBadge(source),
      onConnectivity: (reachable) => this._setConnectivity(reachable),
    });
    this._hlsSession = sess;
    this._seekReady = !!sess.seekReady?.();
    this._applySeekReadyUI(this._seekReady, sess.keyframesEtaMs?.() || 0);
    // KEIN per-Session-Cleanup-Push (Deep-Review-Fix (b)): das liess _cleanups
    // bei jedem respawn um eine stale dispose-Closure wachsen (N redundante
    // Stop-Beacons bei destroy()). Da _dropSession() bei jedem Replace disposed,
    // ist zur destroy()-Zeit nur die AKTUELLE Session offen — die raeumt
    // destroy() ueber ein einmaliges _dropSession() ab.

    // 2026-05-08: für ISO/DVD/BD entdeckt zapd via libdvdread mehr audio-
    // tracks als unser /api/probe (= ffprobe direct sieht nur 1 audio in
    // einer DVD-ISO). Nach session-start fetchen wir zapd's /tracks und
    // updaten _probeAudioStreams + controls-dropdown wenn zapd MEHR tracks
    // hat als der initial-probe — dann kann der user main 5.1 vs commentary
    // stereo umschalten. (Backend's /api/probe liefert für non-ISO-files
    // bereits korrekte daten, dort bleibt's bei dem was wir hatten.)
    try {
      const trackData = await zapdTracks(sess.sessionHash);
      const zapdAudios = Array.isArray(trackData?.audio) ? trackData.audio : [];
      const zapdSubs = Array.isArray(trackData?.subtitles) ? trackData.subtitles : [];
      // Replace dropdown source if zapd has tracks AND either (a) more than
      // initial probe (= ISO/DVD/BD where probe missed tracks) OR (b) zapd
      // has language-tags die initial-probe nicht hatte (= BluRay-iso, weil
      // ffmpeg's bluray-demuxer lang-tags nicht propagiert; zapd merged sie
      // jetzt aus libbluray's BLURAY_STREAM_INFO::lang).
      const zapdHasLangs = zapdAudios.some(
        (a) => a && a.language && String(a.language).toLowerCase() !== 'und' && a.language !== '',
      );
      const probeMissingLangs = (this._probeAudioStreams || []).every(
        (a) => !a.language || String(a.language).toLowerCase() === 'und' || a.language === '',
      );
      const shouldReplace =
        zapdAudios.length > (this._probeAudioStreams?.length || 0) ||
        (zapdAudios.length > 0 && zapdHasLangs && probeMissingLangs);
      if (shouldReplace) {
        this._probeAudioStreams = zapdAudios;
        // Re-run language-auto-pick on zapd's richer track list (= ISO/DVD
        // tracks that the initial probe missed). Wenn die auto-pick einen
        // anderen track findet als der momentan-aktive, switchen wir on
        // the fly — sonst weiter bei track 0.
        if (this._audioTrackAuto) {
          const idx = pickAudioTrackByLanguage(zapdAudios);
          if (idx >= 0 && idx !== this.playerSettings.audioTrack) {
            this.playerSettings.audioTrack = idx;
            try {
              this.setAudioTrack?.(idx);
            } catch {}
          }
        }
        try {
          this._controls?.setStreams?.(
            zapdAudios,
            zapdSubs.length > 0 ? zapdSubs : this._probeSubtitleStreams,
            {
              audioTrack: this.playerSettings?.audioTrack ?? 0,
              subtitleTrack: this.playerSettings?.subtitleTrack ?? -1,
            },
          );
        } catch {}
      }
    } catch (e) {
      console.warn('[zap-player] zapd /tracks fetch failed', e);
    }

    // Defensive playing-detection: SSE-race mit session.phase-event
    // möglich (~200ms early), daher nutzen wir auch <video>'s playing-
    // event als „echtes erstes frame da"-trigger.
    const onPlayingDefensive = () => this._setPhase('playing', null);
    this.video.addEventListener('playing', onPlayingDefensive);
    this._cleanups.push(() => this.video.removeEventListener('playing', onPlayingDefensive));

    // Audio autoplay-mute defensive: Chrome/Safari sometimes start the
    // <video> with muted=true when MSE attaches asynchronously (the
    // user-gesture token has expired by then). On first `playing`, if
    // the user-preference is unmuted, force-unmute. Browsers honor that
    // because we're now post-user-interaction (the click that opened
    // the modal). If the unmute request fails silently, toast.
    const onPlayingUnmute = () => {
      if (!this.playerSettings) return;
      if (this.playerSettings.muted) return; // user actually wants muted
      if (!this.video.muted) return; // already unmuted, nothing to do
      try {
        this.video.muted = false;
        if (this.video.muted) {
          // Browser refused. Don't spam — one toast is enough.
          if (!this._unmuteToastShown) {
            this._unmuteToastShown = true;
            try {
              (window.zapToast || (() => {}))('Klick auf Lautsprecher-Symbol für Ton');
            } catch {}
          }
        }
      } catch {}
    };
    this.video.addEventListener('playing', onPlayingUnmute);
    this._cleanups.push(() => this.video.removeEventListener('playing', onPlayingUnmute));

    // zapd: master.m3u8 hat alle audio + sub renditions schon drin
    // (Phase B + C). Player switcht via audioTracks-/textTracks-API
    // client-side ohne Server-Roundtrip.
    return sess.playlistUrl;
  }

  /** Render the current backend phase under the player. Empty-label phases
   *  ('playing', 'stopped') hide the banner so the media-info slot becomes
   *  the only visible element below the video. */
  /**
   * Seek-readiness von der Session geliefert (siehe lifecycle.js's
   * onSeekReadyChange-Callback). Wenn cold-cache-miss: false initial,
   * dann nach ~3-30s true (zapd's keyframe-scan-thread fertig). UI
   * wird entsprechend aktualisiert: scrub-bar disabled + spinner-overlay
   * solange false.
   */
  _setSeekReady(ready, etaMs) {
    this._seekReady = !!ready;
    this._keyframesEtaMs = Number(etaMs || 0);
    this._applySeekReadyUI(this._seekReady, this._keyframesEtaMs);
  }

  /**
   * Connectivity-Banner (Robustness P1): der /state-Poll meldet, wenn zapd
   * unerreichbar ist (Restart / nginx-Hang) — statt still im Backoff zu
   * haengen, zeigen wir „Verbindung zum Server verloren — neu verbinden…"
   * und raeumen es bei Rueckkehr ab. Reuse des Warmup-Overlays (= „connecting").
   */
  _setConnectivity(reachable) {
    if (reachable) {
      if (this._connLost) {
        this._connLost = false;
        this._hideWarmupOverlay();
      }
    } else if (!this._connLost) {
      this._connLost = true;
      this._showWarmupOverlay('Verbindung zum Server verloren — neu verbinden…', 0, 0);
    }
  }

  /**
   * Handle lifecycle-phase updates from zapd's /state poll.
   * phase ∈ {init, mount, probe, cache_lookup, encode_warmup, ready}.
   * Overlay zeigt eine deutsche message + progressbar während phase ≠ ready,
   * verschwindet bei phase=ready.
   */
  _setLifecyclePhase(phase, message, progressPct, etaMs) {
    this._lifecyclePhase = phase;
    if (phase === 'ready') {
      this._hideWarmupOverlay();
      return;
    }
    // Default-deutsche labels falls server keine message liefert.
    const fallback = {
      init: 'Verbindung herstellen…',
      mount: 'ISO wird gemountet…',
      probe: 'Streams einlesen…',
      cache_lookup: 'Cache prüfen…',
      encode_warmup: 'Transcoding läuft…',
    };
    const label = (message && message.trim()) || fallback[phase] || `Phase: ${phase}`;
    this._showWarmupOverlay(label, progressPct || 0, etaMs || 0);
  }

  /**
   * Z4 (Contract 02 §4.1): Source-Badge fuer Play-while-Download. source =
   * {kind, progress} aus /play/start bzw. dem /state-Poll (lifecycle.js's
   * onSourceChange). Reine Render-Logik liegt entkoppelt in ui/source-badge.js
   * (isoliert testbar); hier nur die Stage-Aufloesung wie beim Warmup-Overlay.
   */
  _setSourceBadge(source) {
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    setSourceBadge(stage, source);
  }

  /** Mount overlay IMMEDIATELY (= called von _attachHlsSource pre-poll).
   *  Nicht warten bis erste poll-response — sonst sieht user „nichts
   *  passiert über sekunden". Initial-state: spinner + „Verbindung…". */
  _showWarmupOverlay(label, progressPct, etaMs) {
    // Embedded mode: once the native engine is playing, NEVER (re-)show a loading
    // overlay. The native video renders BELOW the transparent WebView; an overlay
    // spinner here sits ON TOP of the playing video and — because something keeps
    // toggling it (phase/waiting churn) — flickers + blacks out the picture (the
    // user-reported "loading spinner flackert, bild schwarz"). The engine handles
    // its own buffering; no SPA loading chrome belongs over the native plane.
    if (this._embedded && this._embeddedStarted) return;
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    if (!stage) return;
    let overlay = stage.querySelector('.zap-warmup-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'zap-warmup-overlay';
      overlay.innerHTML =
        '<div class="zap-warmup-spinner"></div>' +
        '<div class="zap-warmup-label" data-slot="label"></div>' +
        '<div class="zap-warmup-progress"><div class="zap-warmup-progress-bar" data-slot="bar"></div></div>' +
        '<div class="zap-warmup-eta" data-slot="eta"></div>';
      stage.appendChild(overlay);
    }
    const labelEl = overlay.querySelector('[data-slot="label"]');
    const barEl = overlay.querySelector('[data-slot="bar"]');
    const etaEl = overlay.querySelector('[data-slot="eta"]');
    if (labelEl) labelEl.textContent = label;
    if (barEl) barEl.style.width = `${Math.max(0, Math.min(100, progressPct))}%`;
    if (etaEl) etaEl.textContent = etaMs > 0 ? `~${Math.ceil(etaMs / 1000)}s verbleibend` : '';
  }

  _hideWarmupOverlay() {
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    if (!stage) return;
    // Wenn duration noch nicht bekannt ist (= time-label pulsiert noch),
    // sind wir trotz phase=ready vom server noch nicht wirklich playable.
    // Overlay weiterhin zeigen statt bigplay zu früh anzubieten.
    const dur =
      this.video && isFinite(this.video.duration) && this.video.duration > 0
        ? this.video.duration
        : 0;
    if (dur <= 0) {
      this._showWarmupOverlay('Fast bereit — Streams initialisieren…', 95, 0);
      // Re-check in 250ms: if duration becomes known we'll proceed.
      if (this._dratWaitTimer) clearTimeout(this._dratWaitTimer);
      this._dratWaitTimer = setTimeout(() => this._hideWarmupOverlay(), 250);
      return;
    }
    if (this._dratWaitTimer) {
      clearTimeout(this._dratWaitTimer);
      this._dratWaitTimer = null;
    }
    const overlay = stage.querySelector('.zap-warmup-overlay');
    if (overlay) overlay.remove();
    if (!this._userInitiatedPlay) this._showBigPlay();
  }

  _showBigPlay() {
    const stage = this.stage || this._modal?.stage || null;
    if (!stage) return;
    if (stage.querySelector('.zap-bigplay')) return; // already shown
    const overlay = document.createElement('div');
    overlay.className = 'zap-bigplay';
    overlay.innerHTML = '<div class="zap-bigplay__btn" aria-label="Play"></div>';
    overlay.addEventListener('click', () => {
      this._userInitiatedPlay = true;
      this._hideBigPlay();
      try {
        this.video?.play?.();
      } catch {
        /* noop */
      }
    });
    stage.appendChild(overlay);
  }

  _hideBigPlay() {
    const stage = this.stage || this._modal?.stage || null;
    if (!stage) return;
    const overlay = stage.querySelector('.zap-bigplay');
    if (overlay) overlay.remove();
  }

  _applySeekReadyUI(ready, etaMs) {
    const root = this._modal?.root || document;
    // Versuche scrub/progress-bar zu finden (zap-player nutzt nativen
    // controls=true aktuell — UI-spinner-overlay an der Stage.)
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    if (!stage) return;
    let overlay = stage.querySelector('.zap-indexing-overlay');
    if (!ready) {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'zap-indexing-overlay';
        overlay.innerHTML =
          '<div class="zap-indexing-spinner"></div>' +
          '<div class="zap-indexing-label">Indexing — Seek wird gleich verfügbar…</div>';
        stage.appendChild(overlay);
      }
      const label = overlay.querySelector('.zap-indexing-label');
      if (label) {
        label.textContent =
          etaMs > 0
            ? `Indexing — Seek in ~${Math.ceil(etaMs / 1000)}s verfügbar`
            : 'Indexing — Seek wird gleich verfügbar…';
      }
      // Mark scrub-bar as not-yet-seekable. Native <video controls>
      // exposes no direct way to disable just the seek-bar, so we
      // capture seek-attempts in seekTo() and ignore them.
      stage.classList.add('zap-seek-disabled');
      // Plus visual feedback on our custom controls scrubber:
      const scrubber = this._modal?.root?.querySelector('.po-controls__scrubber');
      if (scrubber) scrubber.classList.add('is-indexing');
    } else {
      if (overlay) overlay.remove();
      stage.classList.remove('zap-seek-disabled');
      const scrubber = this._modal?.root?.querySelector('.po-controls__scrubber');
      if (scrubber) scrubber.classList.remove('is-indexing');
    }
  }

  /**
   * Zentralisierter seek-Handler. Vorteile gegenüber direktem
   * `video.currentTime = t`:
   *   - debounced bei _seekPending (= ein seek bereits inflight) →
   *     setzt nur den deferred-target, in-flight bleibt aktiv
   *   - blockiert komplett wenn _seekReady=false (cold-cache-miss)
   *   - ghost-progress-feedback (optisch sofortiges feedback)
   */
  seekTo(targetSec) {
    if (!Number.isFinite(targetSec) || targetSec < 0) return;
    if (this._seekReady === false) {
      // Indexing läuft noch — ignoriere user-seek (UI-overlay
      // signalisiert das schon).
      return;
    }
    // 2026-05-08: keyframe-snap disabled. The /play/{sid}/keyframes
    // endpoint exposes SOURCE keyframes (= libavformat AVStream PTS
    // values from the original ISO/MKV/BluRay). Those are NOT 0-aligned
    // for BluRay/MPEG-TS sources (PTS starts at e.g. 4198s for typical
    // BluRay clips). Snapping target=860s to kfArr[0]=4198000000us
    // resulted in landing at minute 70 instead of minute 14 (= the
    // user-reported "snappt immer am anfang"-bug).
    //
    // The orchestrator.reset(target) already finds the segment containing
    // target's playback-time and the encoder's force_key_frames=expr at
    // seg_dur intervals guarantees segment-start IDRs. No client-side
    // snap needed for correctness — server delivers the right segment.
    //
    // Server-side: zapd's encoder seeks to nearest source-IDR before
    // target on its own (av_seek_frame BACKWARD). That's a server-perf
    // optimization, not a client concern.
    if (this._seekPending) {
      // Ein seek ist inflight — speichere nur das letzte target,
      // wird nach `seeked` event angewendet. Overlay bleibt sichtbar.
      this._seekDeferredTarget = targetSec;
      return;
    }
    this._seekPending = true;
    this._seekStartedAt = performance.now();
    this._showSeekingOverlay();
    try {
      this.video.currentTime = targetSec;
    } catch {
      /* noop */
    }
    const finishPending = () => {
      this._seekPending = false;
      const next = this._seekDeferredTarget;
      this._seekDeferredTarget = null;
      if (next != null && Math.abs(next - this.video.currentTime) > 0.5) {
        // Apply deferred target ohne neuen pending-cycle.
        setTimeout(() => this.seekTo(next), 50);
      } else {
        // Kein deferred-Target → overlay verschwindet erst wenn Player
        // wieder läuft (waiting → playing). Dafür hängen wir am
        // <video>-element 'playing' / 'canplay' an.
        this._hideSeekingOverlayWhenPlaying();
      }
    };
    const onSeeked = () => {
      this.video.removeEventListener('seeked', onSeeked);
      finishPending();
    };
    this.video.addEventListener('seeked', onSeeked);
    // Safety-timeout: nach 8s spätestens free-pending (auch wenn no
    // `seeked` event kam — was selten passiert bei fragment-loading).
    setTimeout(() => {
      if (this._seekPending && performance.now() - this._seekStartedAt > 8000) {
        this.video.removeEventListener('seeked', onSeeked);
        finishPending();
      }
    }, 8500);
  }

  /**
   * Zeigt einen kleinen runden Spinner-overlay an der Stage. Anders als
   * `_applySeekReadyUI(false)` (cold-cache-miss, full-screen-overlay
   * mit label-text), ist das eine SCHLANKE visuelle Bestätigung: jeder
   * User-Seek triggert es, auch bei warmem Cache, sodass der User
   * sieht „etwas läuft".
   */
  _showSeekingOverlay() {
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    if (!stage) return;
    let overlay = stage.querySelector('.zap-seeking-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'zap-seeking-overlay';
      overlay.innerHTML = '<div class="zap-seeking-spinner"></div>';
      stage.appendChild(overlay);
    }
    overlay.classList.add('is-visible');
  }

  _hideSeekingOverlay() {
    const stage =
      this.stage ||
      this._modal?.stage ||
      this._modal?.root?.querySelector('.cover-modal__stage') ||
      this._modal?.root?.querySelector('.zap-player-stage') ||
      null;
    if (!stage) return;
    const overlay = stage.querySelector('.zap-seeking-overlay');
    if (overlay) overlay.classList.remove('is-visible');
  }

  /**
   * `seeked` feuert wenn der Browser den seek-Schritt abgeschlossen hat
   * (= currentTime auf dem target ist + readyState ≥ HAVE_METADATA).
   * Aber das heißt NICHT, dass der buffer schon gefüllt ist. Player
   * bleibt oft 1-3s in `waiting`-state. Wir verstecken den overlay
   * erst beim ECHTEN playback-resume (`playing`-event).
   */
  _hideSeekingOverlayWhenPlaying() {
    const onPlaying = () => {
      this.video.removeEventListener('playing', onPlaying);
      this.video.removeEventListener('canplay', onPlaying);
      this._hideSeekingOverlay();
    };
    this.video.addEventListener('playing', onPlaying, { once: true });
    this.video.addEventListener('canplay', onPlaying, { once: true });
    // Safety: nach 5s overlay weg, egal was Player macht.
    setTimeout(() => this._hideSeekingOverlay(), 5000);
  }

  _setPhase(state, detail) {
    const banner = this._phaseBanner;
    if (!banner) return;
    const label = phaseLabel(state);
    banner.dataset.phase = state || '';
    if (!label) {
      banner.hidden = true;
      banner.innerHTML = '';
      return;
    }
    banner.innerHTML =
      '<span class="zap-hls-spinner" aria-hidden="true"></span>' +
      '<span class="zap-hls-phase-text"></span>';
    const txt = banner.querySelector('.zap-hls-phase-text');
    if (txt) txt.textContent = detail ? label + ' – ' + String(detail) : label;
    banner.hidden = false;
  }

  _handleSuperseded(reason) {
    const banner = this._phaseBanner;
    if (banner) {
      banner.hidden = false;
      banner.innerHTML = '';
      const span = document.createElement('span');
      span.className = 'zap-hls-phase-text';
      span.style.color = '#f5a623';
      span.textContent = 'Stream wurde übernommen' + (reason ? ' (' + reason + ')' : '');
      banner.appendChild(span);
    }
    setTimeout(() => {
      try {
        this.destroy();
      } catch {}
    }, 1500);
  }

  /** Single owner for "drop the current HLS session" (Robustness P1): dispose()
   *  faehrt die Teardown-Kette (heartbeat, sync stop, lifecycle-hooks) + nullt
   *  das Handle. Idempotent. Ersetzt ~7 inline-Kopien. Die Serialisierung gegen
   *  parallele Sessions liegt in den In-Flight-Guards (_startInFlight in
   *  _startSession, _reattachInFlight in _reattachPreservingTime). */
  _dropSession() {
    if (this._hlsSession) {
      try {
        this._hlsSession.dispose();
      } catch {
        /* noop */
      }
      this._hlsSession = null;
    }
  }

  /** Settings-Change (Quality/AudioNorm/Sub/AudioMode/Audio-Fallback) braucht
   *  eine NEUE HLS-Session (Encoder-Argumente aendern sich): alte droppen, dann
   *  zeit-erhaltend reattachen (_reattachPreservingTime ist single-flight). */
  _respawnSession() {
    // Capture a STABLE resume position the first time a respawn is requested
    // while playback is healthy, BEFORE any teardown. Sampling
    // video.currentTime later (mid-warmup of a fresh session) reads ~0.
    if (this._respawnResumeSec == null) {
      const ct = this.video?.currentTime || 0;
      if (ct > 1) this._respawnResumeSec = ct;
    }
    // If a reattach is already in flight, DON'T _dropSession() — that nulls
    // _hlsSession out from under the in-flight _attachHlsSource (-> throw at
    // `_hlsSession.playlistUrl` -> wedge). Its settings-drift recheck
    // re-respawns with the latest settings once it completes.
    if (this._reattachInFlight) return;
    // Coalesce rapid switches (2026-06-15): debounce so multiple settings
    // changes within the window collapse into ONE reattach with the FINAL
    // settings + the position captured above. The previous per-switch respawn
    // created a fragile multi-session chain (each fresh session re-raced the
    // resume-seek during warmup), which converged to position 0 / wedged on
    // rapid back-to-back switches. Playback keeps running untouched until the
    // debounce fires.
    clearTimeout(this._respawnTimer);
    this._respawnTimer = setTimeout(() => {
      this._respawnTimer = null;
      if (this._destroyed) return;
      this._dropSession();
      this._reattachPreservingTime();
    }, 350);
  }

  /** Signatur aller encoder-relevanten Settings — fuer den Reattach-Drift-
   *  Recheck (Deep-Review (a)): aendert sich eine davon waehrend eines
   *  in-flight Reattach, muss danach neu reattacht werden. */
  _settingsSignature() {
    const s = this.playerSettings || {};
    return [s.quality, s.audionorm, s.audioMode, s.subtitleTrack, s.audioTrack].join('|');
  }

  _reattachPreservingTime() {
    // Single-flight + deferred target. Multiple seeks during a long reattach
    // cycle would otherwise spawn parallel detach/attach pairs, leaving the
    // video element in an undefined state. When a reattach is in flight we
    // just record the latest seek target and let the in-flight reattach
    // jump there at the end.
    //
    // Plan v2 (2026-05-02) Race-Fix: a quality-change while a reattach is
    // already in flight must NOT be lost. We snapshot the current quality
    // when the reattach starts (`startedQuality`) and re-check after restore.
    // If the user's `playerSettings.quality` has moved on by then, we
    // trigger a fresh reattach with the new value.
    const target = this._respawnResumeSec ?? (this.video.currentTime || 0);
    // Snapshot play-state BEFORE the source detach: after the new source
    // attaches the <video> is paused (a fresh MSE source always starts in
    // paused state), so without this the user has to manually click play
    // every time we reattach (far-seek, audio-track switch, sub-track
    // switch, quality change). Restoring `play()` after `currentTime` is
    // set keeps the experience continuous.
    const wasPlaying = !this.video.paused && !this.video.ended;
    if (this._reattachInFlight) {
      this._reattachDeferredTarget = target;
      return;
    }
    this._reattachInFlight = true;
    this._reattachDeferredTarget = target;
    // Signatur ALLER encoder-relevanten Settings bei Reattach-Start (Deep-
    // Review-Fix (a)): nicht nur quality — sonst geht ein AudioNorm/Sub/
    // AudioMode/AudioTrack-Wechsel waehrend des Reattach verloren.
    const startedSig = this._settingsSignature();
    Promise.resolve(this._maybeAttachSource())
      .then(() => {
        const restore = () => {
          const tgt = this._reattachDeferredTarget ?? target;
          try {
            this.video.currentTime = tgt;
          } catch {
            /* noop */
          }
          this._reattachDeferredTarget = null;
          this.video.removeEventListener('loadedmetadata', restore);
          this._reattachInFlight = false;
          if (wasPlaying) {
            // play() returns a rejected promise on autoplay-policy violations
            // (e.g. tab lost focus during the reattach); silently swallow —
            // the visible play-button still works.
            this.video.play().catch(() => {
              /* noop */
            });
          }
          // Re-assert the resume position (2026-06-15): the seek above is issued
          // on `loadedmetadata` (= engine 'ready'), but the freshly-attached
          // worker may not have its playlist yet — BufferOrchestrator.reset()
          // no-ops without a playlist, so the seek is silently dropped and the
          // new session plays from 0 (= the subtitle/audio-mode/quality switch
          // "loses my place" bug). Re-issue the seek a few times until the clock
          // catches up. Only re-seek when the clock fell FAR behind the target
          // (dropped seek), never when it advanced past it via normal playback.
          if (tgt > 1) {
            let tries = 4;
            const reassert = () => {
              // Bail if destroyed or a newer reattach took over (e.g. a rapid
              // second settings-switch) — that reattach owns the position now.
              if (--tries < 0 || this._destroyed || this._reattachInFlight) return;
              if ((this.video.currentTime || 0) < tgt - 5) {
                try {
                  this.video.currentTime = tgt;
                } catch {
                  /* noop */
                }
                setTimeout(reassert, 700);
              }
            };
            setTimeout(reassert, 700);
          }
          // Race-recheck: hat sich waehrend des Attach IRGENDEINE encoder-
          // relevante Einstellung geaendert (nicht nur quality)? Dann die frisch
          // attachte Session droppen + mit den aktuellen Settings neu reattachen.
          if (this._settingsSignature() !== startedSig) {
            this._respawnSession();
          } else {
            // Chain settled on the latest settings — release the threaded
            // resume position so the next switch captures a fresh one.
            this._respawnResumeSec = null;
          }
        };
        this.video.addEventListener('loadedmetadata', restore);
      })
      .catch(() => {
        this._reattachInFlight = false;
        this._reattachDeferredTarget = null;
        this._respawnResumeSec = null;
      });
  }

  // ── Public transport API ──────────────────────────────────────────────
  setQuality(q) {
    this.playerSettings.quality = q;
    setPlayerSetting('Quality', q);
    // Visible feedback so the user doesn't think the player froze during
    // the 200-2000 ms POST /api/hls/start round-trip + ffmpeg cold-start.
    try {
      this._setPhase?.('spawning', 'Wechsele Qualität…');
    } catch {}
    // Quality-change must spawn a NEW HLS session because the encoder
    // arguments differ — drop the existing session so _attachHlsSource
    // starts a fresh one. dispose() runs the cleanup chain (heartbeat,
    // sendBeacon, lifecycle hooks) for the old session.
    this._respawnSession();
  }

  setAudioNorm(on) {
    this.playerSettings.audionorm = !!on;
    setPlayerSetting('AudioNorm', on ? '1' : '0');
    this._respawnSession();
  }

  /** Switch audio track. N is the array-index into _probeAudioStreams. */
  setAudioTrack(n) {
    const idx = Math.max(0, parseInt(n, 10) || 0);
    if (idx === this.playerSettings.audioTrack) return;
    // Remember the currently-playing track so _onAudioSwitchFailed can
    // revert the selection if the switch can't complete.
    this._audioTrackBeforeSwitch = this.playerSettings.audioTrack;
    this.playerSettings.audioTrack = idx;
    setPlayerSetting('AudioTrack', String(idx));
    // Track-switch via POST /switch — server entscheidet zwischen
    // audio-only-swap (Phase 8 swapAudio, video läuft weiter) und
    // gen-bump (sub-track-change). Frontend ruft entsprechend
    // setAudioRendition oder _reattachPreservingTime.
    const track = this._probeAudioStreams?.[idx];
    const wantStreamIdx = track && Number.isFinite(track.index) ? track.index : null;
    // Embedded mode has no server HLS session — the native engine switches the
    // stream live via the bridge (selectAudioTrack), no re-open/re-buffer. Route
    // here instead of falling through to the (session-based) server path, which
    // would otherwise hit the full-reattach fallback.
    if (this._embedded) {
      if (wantStreamIdx != null) {
        try {
          this._zapdBridge?.setTracks?.({ audio: wantStreamIdx });
        } catch {
          /* noop */
        }
      }
      try {
        this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
          audioTrack: idx,
          subtitleTrack: this.playerSettings?.subtitleTrack ?? -1,
        });
      } catch {
        /* noop */
      }
      return;
    }
    // A-P1.1 direct mode: the demuxer already has ALL tracks locally — repoint
    // the worker's audio decoder in place, no server round-trip. An unsupported
    // codec surfaces as 'audioSwitchFailed' → _onAudioSwitchFailed reverts.
    if (this._zapdBridge?.getMode?.() === 'direct') {
      if (wantStreamIdx != null) {
        try {
          this._zapdBridge.setDirectAudioTrack(wantStreamIdx);
        } catch {
          /* noop */
        }
        try {
          this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
            audioTrack: idx,
            subtitleTrack: this.playerSettings?.subtitleTrack ?? -1,
          });
        } catch {
          /* noop */
        }
      } else {
        this._fallbackSetAudioTrack();
      }
      return;
    }
    const sid = this._hlsSession?.sessionHash;
    if (sid && wantStreamIdx != null) {
      this._serverSwitchAudioTrack(sid, wantStreamIdx).catch((e) => {
        console.warn('[zap-player] switchTracks failed, falling back to reattach', e);
        this._fallbackSetAudioTrack();
      });
      return;
    }
    // Fallback: keine session/idx → full reattach.
    this._fallbackSetAudioTrack();
  }

  async _serverSwitchAudioTrack(sid, audioStreamIdx) {
    try {
      this._setPhase?.('spawning', 'Wechsele Audiospur…');
    } catch {}
    const position = Number.isFinite(this.video?.currentTime)
      ? Math.max(0, this.video.currentTime)
      : 0;
    // Phase 8: nur `audio` schicken (NICHT `sub`) — server detected dadurch
    // den audio-only-pfad und swappt audio-pipeline ohne gen-bump.
    const resp = await switchTracks(sid, { audio: audioStreamIdx, position });
    // CONCURRENCY GUARD (Klasse 4): a gen_bump/respawn (setQuality/sub/audio-
    // mode) may have dropped or replaced the session while our /switch was in
    // flight. Applying this OLD-gen audio-rendition to the NEW bridge corrupts
    // the fresh session's audio pipeline -> wedge (paused, frozen). Bail — the
    // reattach already loads the new gen's audio-rendition from its master.m3u8.
    if (
      this._destroyed ||
      this._reattachInFlight ||
      this._respawnTimer ||
      this._hlsSession?.sessionHash !== sid
    ) {
      try {
        this._setPhase?.('ready', '');
      } catch {
        /* noop */
      }
      return;
    }
    if (resp && resp.mode === 'audio_swap' && resp.audio_rendition_url) {
      // Audio-swap sync-fix S3 (2026-05-10): use CURRENT video.currentTime,
      // NOT the stale `position` captured at click-time. The shadow's
      // /switch round-trip + catch-up takes ~500ms-1s, during which video
      // advances. Anchoring at stale `position` would cause master-clock
      // to jump backwards by that delay when audio re-anchors. Sync-touch
      // S3 + S4 in plan: pass current time → scheduler.setNextAnchor →
      // first buffer plays from offset that matches current master-clock.
      const absUrl = new URL(resp.audio_rendition_url, window.location.href).toString();
      const positionNow = Number.isFinite(this.video?.currentTime)
        ? Math.max(0, this.video.currentTime)
        : position;
      try {
        this._zapdBridge?.setAudioRendition?.(absUrl, positionNow);
      } catch (e) {
        console.warn('[zap-player] setAudioRendition failed, falling back to reattach', e);
        this._reattachPreservingTime();
      }
      // Phase wieder aufräumen — kein full-warmup-zyklus, video läuft.
      try {
        this._setPhase?.('ready', '');
      } catch {}
      return;
    }
    // Fallback: server returned full-gen-bump response (oder altes API).
    // Reattach lädt neues master.m3u8 inkl. video-rendition aus new gen.
    this._reattachPreservingTime();
  }

  _fallbackSetAudioTrack() {
    try {
      this._setPhase?.('spawning', 'Wechsele Audiospur…');
    } catch {}
    this._respawnSession();
  }

  /** Engine reported the audio-track switch couldn't complete. Revert the
   *  dropdown selection to the previously-playing track (which the engine
   *  keeps alive where possible) and toast. Video is unaffected. */
  _onAudioSwitchFailed(reason) {
    console.warn('[zap-player] audio-switch failed:', reason);
    const prev = Number.isFinite(this._audioTrackBeforeSwitch) ? this._audioTrackBeforeSwitch : 0;
    this.playerSettings.audioTrack = prev;
    try {
      setPlayerSetting('AudioTrack', String(prev));
    } catch {}
    try {
      this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
        audioTrack: prev,
        subtitleTrack: this.playerSettings?.subtitleTrack ?? -1,
      });
    } catch {}
    try {
      this._setPhase?.('ready', '');
    } catch {}
    try {
      toast.warn('Audiospur konnte nicht gewechselt werden');
    } catch {}
  }

  /** Switch subtitle track. N is the absolute container stream index, -1 = off.
   *  Triggert /switch mit `sub`-feld → server gen-bump + reattach. */
  setSubtitleTrack(n) {
    const idx = parseInt(n, 10);
    const next = Number.isFinite(idx) ? idx : -1;
    if (next === this.playerSettings.subtitleTrack) return;
    const prev = this.playerSettings.subtitleTrack;
    this.playerSettings.subtitleTrack = next;
    setPlayerSetting('SubtitleTrack', String(next));
    // Embedded mode: the native engine selects the subtitle stream live via the
    // bridge (selectSubtitleTrack; next=-1 turns it off) and emits cues to the
    // DOM cue-overlay — no server session / respawn (which embedded lacks). Route
    // here before the browser SubtitleController / fallback-respawn paths.
    if (this._embedded) {
      try {
        this._zapdBridge?.setTracks?.({ sub: next });
      } catch {
        /* noop */
      }
      try {
        this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
          audioTrack: this.playerSettings?.audioTrack ?? 0,
          subtitleTrack: next,
        });
      } catch {
        /* noop */
      }
      return;
    }
    // F1 W4: Im Browser-Pfad wechselt die Sidecar-WebVTT-Rendition in-place
    // ueber den SubtitleController — KEIN Session-Respawn, Video/Audio laufen
    // ununterbrochen weiter (das vom User gemeldete "alles laedt erneut" faellt
    // damit weg). Nur der Shell-/Fallback-Pfad braucht den /switch-gen-bump.
    if (this._subtitleController) {
      const applyDropdown = (sub) => {
        try {
          this._controls?.setStreams?.(this._probeAudioStreams, this._probeSubtitleStreams, {
            audioTrack: this.playerSettings?.audioTrack ?? 0,
            subtitleTrack: sub,
          });
        } catch {
          /* noop */
        }
      };
      applyDropdown(next);
      this._subtitleController
        .setTrack(next)
        .then((ok) => {
          // Reconcile UI with reality: if the rendition couldn't load, revert the
          // persisted selection + dropdown and toast (analogous to audio).
          if (ok === false && next >= 0) {
            this.playerSettings.subtitleTrack = prev;
            setPlayerSetting('SubtitleTrack', String(prev));
            applyDropdown(prev);
            try {
              toast.warn('Untertitel konnten nicht geladen werden');
            } catch {}
          }
        })
        .catch((e) => console.warn('[subtitles] track switch failed:', e?.message || e));
      return;
    }
    // 2026-05-04: switching sub tracks DOES trigger a full HLS-session respawn
    // (dispose + new /api/hls/start) on the shell/fallback path; show the
    // spawning banner like setAudioTrack does so the screen isn't blank during
    // the reattach with no feedback.
    try {
      this._setPhase?.('spawning', next < 0 ? 'Untertitel aus…' : 'Wechsele Untertitel…');
    } catch {}
    this._respawnSession();
  }

  /** Switch audio mode: 'stereo' | 'surround' | 'passthrough'. Phase B (audio
   *  modes) consumes this; for now only stereo is exercised. */
  setAudioMode(mode) {
    if (!['stereo', 'surround', 'passthrough'].includes(mode)) return;
    if (mode === this.playerSettings.audioMode) return;
    this.playerSettings.audioMode = mode;
    setPlayerSetting('AudioMode', mode);
    try {
      this._setPhase?.('spawning', 'Wechsele Audio-Modus…');
    } catch {}
    this._respawnSession();
  }

  setSpeed(rate) {
    const r = parseFloat(rate);
    if (!isFinite(r) || r <= 0) return;
    this.video.playbackRate = r;
    this.playerSettings.speed = r;
    setPlayerSetting('Speed', String(r));
  }

  setLoop(on) {
    this.video.loop = !!on;
    this.playerSettings.loop = !!on;
    setPlayerSetting('Loop', on ? '1' : '0');
    this._toolbar?.setLoopState(!!on);
    this._settings?.setLoop(!!on);
  }

  setFadeSkip(on) {
    this.playerSettings.fadeSkip = !!on;
    setPlayerSetting('FadeSkip', on ? '1' : '0');
    // Rebuild fader so the toggle takes effect immediately
    this.fadeAndSkip =
      this.features.fade && on
        ? createFader(this.video)
        : (t) => {
            try {
              this.video.currentTime = t;
            } catch {
              /* noop */
            }
          };
  }

  async togglePip() {
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (this.video.requestPictureInPicture) await this.video.requestPictureInPicture();
    } catch {
      /* user cancelled or unsupported */
    }
  }

  /** Toggle play/pause. Used by the custom controls bar (no native button). */
  togglePlayPause() {
    if (!this.video) return;
    if (this.video.paused || this.video.ended) {
      this._userInitiatedPlay = true;
      this._hideBigPlay();
      this.video.play().catch(() => {
        /* user gesture missing */
      });
    } else {
      this.video.pause();
    }
  }

  /** Toggle stage-fullscreen. Routing via stage means the controls bar +
   *  phase banner stay visible inside the fullscreen layer. */
  toggleFullscreen() {
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen?.();
      } catch {}
    } else if (this.stage?.requestFullscreen) {
      try {
        this.stage.requestFullscreen();
      } catch {}
    }
  }

  /** Stats/debug overlay toggle — drives the same zapd debug-overlay as the
   *  D key (window.toggleZapdDebug, mounted by the zapd-engine bridge). Wired
   *  to the control-bar stats button so the overlay is discoverable, not only
   *  a hidden keyboard shortcut. No-op when the overlay isn't mounted (e.g. a
   *  non-zapd playback path). */
  toggleStats() {
    try {
      window.toggleZapdDebug?.();
    } catch {
      /* noop */
    }
  }
  isStatsVisible() {
    const el = this.stage?.querySelector('[data-zap-debug]');
    return !!(el && !el.hidden);
  }

  /** Editor-Mode toggle (Plan v2 2026-05-02). Bound to the E key in
   *  core/keyboard.js. Hides/shows the marker-strip; F still controls
   *  fullscreen so user-memory stays intact. */
  toggleEditorMode() {
    this._editorMode = !this._editorMode;
    if (this.strip) this.strip.hidden = !this._editorMode;
    return this._editorMode;
  }
  isEditorMode() {
    return !!this._editorMode;
  }

  // ── Render orchestration ──────────────────────────────────────────────
  renderStrip = () => {
    if (!this.strip) return;
    renderStripContent({
      video: this.video,
      strip: this.strip,
      regions: this.regions,
      timeline: this.timeline,
      loopState: this.loop.loopState,
      loopStart: this.loop.loopStart,
      loopEnd: this.loop.loopEnd,
      activeRegion: this.engineState.activeRegion,
      fps: this.videoFps,
    });
  };

  updateBanner = () => {
    if (!this.banner) return;
    renderBanner(this.banner, this.loop, () => this._convertLoopToRegion());
    this.renderStrip();
  };

  _convertLoopToRegion() {
    const { loopStart, loopEnd } = this.loop;
    if (loopEnd <= loopStart + 0.5) return;
    const mode = this._editorSettings.defaultMode || 'skip';
    const region = {
      start: +loopStart.toFixed(2),
      end: +loopEnd.toFixed(2),
      enabled: true,
      label: 'Region ' + (this.regions.length + 1),
      mode,
      repeat_count: mode === 'repeat' ? this._editorSettings.defaultRepeatCount : null,
      speed: null,
    };
    this.regions.push(region);
    this._writeCache();
    srvCreate(this.rawPath, region);
    this.loop.loopState = 'none';
    this.updateBanner();
  }

  _writeCache = () => writeRegionCache(this.rawPath, this.regions);

  // ── Wire up timeupdate / strip / keyboard ─────────────────────────────
  _wireEvents() {
    const onTimeUpdate = () => this._onTimeUpdate();
    this.video.addEventListener('timeupdate', onTimeUpdate);
    this._cleanups.push(() => this.video.removeEventListener('timeupdate', onTimeUpdate));

    const onLoaded = () => this.renderStrip();
    this.video.addEventListener('loadedmetadata', onLoaded);
    this._cleanups.push(() => this.video.removeEventListener('loadedmetadata', onLoaded));
    if (this.video.readyState >= 1) this.renderStrip();

    attachStripHandlers({
      strip: this.strip,
      video: this.video,
      regions: this.regions,
      timeline: this.timeline,
      loop: this.loop,
      rawPath: this.rawPath,
      writeCache: this._writeCache,
      renderStrip: this.renderStrip,
      updateBanner: this.updateBanner,
      setHoverT: (t) => {
        this.lastHoverT = t;
      },
      openPopover: (idx) =>
        openRegionPopover({
          popover: this.popover,
          stage: this.stage,
          strip: this.strip,
          regions: this.regions,
          idx,
          timeline: this.timeline,
          rawPath: this.rawPath,
          writeCache: this._writeCache,
          renderStrip: this.renderStrip,
        }),
    });

    const detachKey = attachKeyboard({
      video: this.video,
      stage: this.stage,
      handleB: () => this._handleB(),
      handleSpace: () => this._handleSpace(),
      handleHelp: () => this.openHelp(),
      handlePip: () => this.togglePip(),
      handleDebug: () => {
        try {
          window.toggleZapdDebug?.();
        } catch {
          /* noop */
        }
      },
      handleEditor: () => this.toggleEditorMode(),
      renderStrip: this.renderStrip,
      editorSettings: () => this._editorSettings,
      fps: () => this.videoFps,
      // zapd-engine bridge for direct frame-step (worker-pipeline aware).
      engineBridge: () => this._zapdBridge,
      timelineState: this.timeline,
    });
    this._cleanups.push(detachKey);
  }

  _handleB() {
    if (this.loop.loopState === 'looping') {
      this.loop.loopState = 'none';
      this.updateBanner();
      return;
    }
    this.loop.loopStart = this.lastHoverT !== null ? this.lastHoverT : this.video.currentTime;
    this.loop.loopState = 'markerSet';
    this.updateBanner();
  }

  _handleSpace() {
    if (this.loop.loopState === 'markerSet') {
      const candidate = this.lastHoverT !== null ? this.lastHoverT : this.video.currentTime;
      if (candidate > this.loop.loopStart + 0.5) {
        this.loop.loopEnd = candidate;
        this.loop.loopState = 'looping';
        try {
          this.video.currentTime = this.loop.loopStart;
        } catch {
          /* noop */
        }
        if (this.video.paused)
          this.video.play().catch(() => {
            /* noop */
          });
      } else {
        this.loop.loopState = 'none';
      }
      this.updateBanner();
      return;
    }
    if (this.loop.loopState === 'looping') {
      this.loop.loopState = 'none';
      this.updateBanner();
      return;
    }
    this.video.paused ? this.video.play() : this.video.pause();
  }

  _onTimeUpdate() {
    const t = this.video.currentTime;
    const cursor = this.strip?.querySelector('.zap-pe-cursor');
    if (cursor && this.video.duration) {
      const vd = (this.video.duration || 0) / Math.max(1, this.timeline.zoomLevel || 1);
      const leftT = Math.max(
        0,
        Math.min((this.video.duration || 0) - vd, this.timeline.panOffsetT || 0),
      );
      cursor.style.left = (((t - leftT) / vd) * 100).toFixed(3) + '%';
    }

    if (autoPanDuringPlayback(this.timeline, t)) this.renderStrip();

    // Z3 Skip-Vorschlag: VOR den Loop/Engine-early-returns, damit der
    // Button auch ein-/ausblendet, waehrend Loop oder Region-Engine aktiv
    // sind. enabled-Regionen behandelt weiterhin exklusiv die Engine unten.
    this._skipUi?.update(this.regions, t);

    if (this.loop.loopState === 'looping' && t >= this.loop.loopEnd - 0.05) {
      try {
        this.video.currentTime = this.loop.loopStart;
      } catch {
        /* noop */
      }
      return;
    }
    const engineCtx = {
      video: this.video,
      fadeAndSkip: this.fadeAndSkip,
      renderStrip: this.renderStrip,
      state: this.engineState,
    };
    if (this.engineState.activeRegion) {
      handleActiveRegion(engineCtx, t);
      return;
    }
    if (
      this.loop.loopState === 'none' &&
      this._editorSettings.showRegions &&
      this.features.regions
    ) {
      for (const r of this.regions) {
        if (!r.enabled) continue;
        if (t >= r.start - 0.02 && t < r.end - 0.1) {
          enterRegion(engineCtx, r);
          break;
        }
      }
    }
  }

  // ── Server sync (regions + fps) ───────────────────────────────────────
  async _initialServerSync() {
    if (!this.rawPath) return;
    const d = await srvList(this.rawPath);
    if (!d) return;
    const fps = d.data?.fps ?? d.fps;
    if (fps && fps > 0) this.videoFps = fps;
    const regions = d.data?.regions ?? d.regions;
    if (Array.isArray(regions)) {
      this.regions.splice(0, this.regions.length, ...regions);
      this._writeCache();
      this.renderStrip();
    }
  }

  // ── Public API ────────────────────────────────────────────────────────
  setSource(streamUrl, ext) {
    this.opts.streamUrl = streamUrl;
    this.opts.ext = ext;
    this._maybeAttachSource();
  }

  loadRegions() {
    return this._initialServerSync();
  }

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    try {
      this._abort.abort();
    } catch {
      /* noop */
    } // in-flight Control-Calls abbrechen
    this._cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        /* noop */
      }
    });
    this._cleanups = [];
    this._dropSession(); // aktuelle HLS-Session disposen (Stop-Beacon), Deep-Review-Fix (b)
    if (this._hlsDetach) {
      try {
        this._hlsDetach();
      } catch {
        /* noop */
      }
      this._hlsDetach = null;
    }
    this.banner?.remove();
    this.strip?.remove();
    this.popover?.remove();
    this._settings?.panel.remove();
    if (!this._modalMode) this._toolbar?.hostEl?.remove();
    // Modal close is idempotent (closed-flag) so this is safe even if the
    // user already clicked the backdrop (which calls back into destroy()).
    this._modal?.close();
    this._modal = null;
    this.opts.onClose?.();
  }
}
